import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { manpowerApi } from '@/lib/apiClient';
import { fmtDate, todayInputValue } from '@/lib/dates';
import { startMismatch, startMismatchSummary } from '@/lib/startAlignment';
import {
  invalidateAssignmentQueries,
  invalidateTravelJobQueries,
  invalidateReadinessQueries,
  invalidateStartAlignmentQueries,
  invalidateManpowerConfigQueries,
} from '@/lib/queryClient';
import LeadersView from './LeadersView';
import HistoryView from './HistoryView';
import RequestsView from './RequestsView';
import GapAnalysisView from './GapAnalysisView';
import { openCountLabel } from './lib/requestLifecycle';

/** Sub-views of the Manpower area, each with its own route so links are shareable. */
type ViewMode = 'schedule' | 'requests' | 'leaders' | 'gap' | 'history';

/** Top-bar labels. The heading and the toggle read from one place so they can't drift. */
const VIEW_LABELS: Record<ViewMode, { toggle: string; heading: string }> = {
  schedule: { toggle: 'Crew Schedule',  heading: 'Crew Schedule' },
  requests: { toggle: 'Requests',       heading: 'Manpower Requests' },
  leaders:  { toggle: 'Leaders',        heading: 'Field Leaders' },
  gap:      { toggle: 'Gap Analysis',   heading: 'Gap Analysis' },
  history:  { toggle: 'History',        heading: 'Schedule History' },
};

/**
 * Toggle order, and it is not the same as the type union's.
 *
 * Requests sits second, beside the grid, because the two are the same job: read
 * what was asked for, then book it. Leaders, Gap Analysis and History are
 * reference views and belong after both.
 *
 * ⚠️ THERE IS NO SEPARATE TRAVEL TAB ANY MORE. The roster moved inside Gap
 * Analysis, behind a toggle at the bottom, because it is reference material
 * rather than a headline — and, since supply became the manpower lead's typed boxes, it no
 * longer feeds a single figure on that page. `/manpower/travel` still resolves
 * so old links and bookmarks land somewhere sensible.
 */
const VIEW_ORDER: readonly ViewMode[] = ['schedule', 'requests', 'leaders', 'gap', 'history'];
import ImportModal from './ImportModal';
import SyncModal from './SyncModal';
import CrewSetupModal, { type CrewSetupResult } from './CrewSetupModal';
import { useScheduleData, type RowSort } from './hooks/useScheduleData';
import {
  addDays, getWeekSunday, localYmd, formatRangeLabel, easternToday,
  buildColumns, weekGroups, isDailyGranularity, RANGE_PRESETS, isWeekendYmd,
  type GridColumn, type RangeWeeks,
} from './lib/gridDates';
import { windowFor, parseYmd } from './lib/gridJump';
import GridJumpControl from './GridJumpControl';
import { planShiftedRun } from './lib/shiftPlan';
import { shiftNotice } from './lib/shiftNotice';
import ExportMenu from './ExportMenu';
import { exportScheduleXlsx, type ScheduleExportInput } from './lib/manpowerExport';
import {
  crewFillStyle, TH, TD, NAV_BTN, TODAY_BTN,
  UNMATCHED_BADGE, SYSTAC_BADGE, NEEDS_SCHEDULING_BADGE, MAINTENANCE_BADGE, LOAN_BADGE,
  START_MISMATCH_BADGE, START_MISMATCH_ROW_BG, START_MISMATCH_ROW_ACCENT,
  LABEL_COLS,
} from './lib/crewStyles';
import {
  type ProjectRow, type ScheduledSurveyRequest, type CrewRow, type CrewPhase,
  isImportStubJobNumber, getTotalledCrewCount, getPhaseCrewCount, buildCrewRows,
  isFabProject, nullPhaseEntryFor, getPhaseCellMeta, stepperTargetDays, stepperValue,
  resolveScope, type ScheduleScope, describeNarrowings, exportRowFlags,
  systacForecast, forecastCrewOn, isForecastRow, getCrewCount, showsForecastCrew,
} from './lib/scheduleModel';
import { PHASE_META, weightedOverall } from '@/lib/schedule';

/**
 * How a SYSTAC bid forecast reads on the grid.
 *
 * It used to render muted and italic (#330), outside the crew fill scale, so it
 * read as an estimate rather than booked crew. That styling is gone (#425): a
 * forecast now paints and counts exactly like booked crew, because it is a real
 * claim on the same capacity and the whole purpose of the SYSTAC toggle is to
 * ask "what if every one of these lands". A number the totals ignore cannot
 * answer that (product owner, 2026-08-31).
 *
 * The tooltip stays, and is now the only thing that distinguishes a forecast
 * cell — along with the row's own SYSTAC badge and tint, which already say the
 * whole job is speculative.
 */
const FORECAST_TITLE =
  'Anticipated crew for the whole job from the bid, not scheduled work. Comes '
  + 'from the crew size and duration the estimator entered in Bid Tracker when '
  + 'the job went SYSTAC.';
import FabPhaseTotalsRows from './FabPhaseTotals';
import AssignmentTray from './AssignmentTray';
import TempsActiveBox from './TempsActiveBox';
import SubcontractorsActiveBox from './SubcontractorsActiveBox';
import ScheduleHelpModal from './ScheduleHelpModal';
import { useDraftEdits, type DraftEntryInput } from './hooks/useDraftEdits';
import SelectionBar, { BulkReportPanel, type BulkReport } from './SelectionBar';
import {
  type Selection, type CellRef, type CellWrite, type Refusal,
  singleCellRefusal, outsideFabScope, rowJobLabel, jobNumbersById, selectionBounds, selectionDays, selectedCells,
  skippedWeekendCount, placeBlock, partitionWrites, describeRefusals, REFUSAL_TEXT,
} from './lib/cellSelection';
import { planBulkWrites, planTypedCell, copyBlock, BULK_DRAFT_MAX_ENTRIES } from './lib/bulkCellWrites';
import { isBlockPaste, parseClipboard, parseCrewValue, toClipboardText } from './lib/crewClipboard';

/**
 * What ONE CREW LINE shows on ONE DAY — booked crew, else the bid forecast.
 *
 * ⚠️ Module scope and shared on purpose. The grid cell and the export both need
 * this number, and the rule has three parts that are easy to get subtly wrong:
 * a real entry always wins over a forecast; the suppression is judged on the
 * PROJECT's crew for the day, not the line's (crew booked against fabrication
 * silences the forecast on installation too); and a fab job's single whole-job
 * bid figure lands on the fabrication line only, or an empty pursuit shows it
 * twice and reads as double the crew. Two copies of that would drift, and the
 * drift would be invisible — a workbook quietly disagreeing with the screen it
 * came from.
 */
function crewLineValue(crewRow: CrewRow, ds: string): number {
  const project = crewRow.project;
  const forecast = isForecastRow(crewRow) ? systacForecast(project) : null;
  const booked = getPhaseCrewCount(crewRow, ds);
  const dayForecast = getCrewCount(project, ds) > 0 ? null : forecastCrewOn(forecast, ds);
  return booked > 0 ? booked : (dayForecast ?? 0);
}

/**
 * The table sizing that applies ON PAPER — emitted twice, from one definition.
 *
 * ⚠️ Once inside `@media print`, and once behind `.mp-measuring`, which
 * printFit.ts switches on for the instant it measures how wide the page will
 * be. Two hand-written copies would drift, and the drift would be silent: the
 * measurement would be taken under different rules than the ones that print,
 * so the computed zoom would fit a layout nobody ever sees.
 *
 * ⚠️ `min-width: 0` on the cells is load-bearing, not tidying. The four label
 * columns carry inline floors (Project 140, PM 90, Type 66) sized for a mouse
 * pointer; on paper they are dead width the day columns need. Measured at the
 * four-week, 32-column view: min-content 1217px → 1134px, which at the fitted
 * zoom is the difference between 9px of headroom on the sheet and 74px.
 */
const COMPACT_TABLE_CSS = (prefix: string) => `
  ${prefix} .mp-fit { width: 100% !important; min-width: 0 !important; }
  ${prefix} .mp-fit th, ${prefix} .mp-fit td {
    padding: 3px 4px !important;
    font-size: 9.5px !important;
    min-width: 0 !important;
  }
`;

/**
 * A row's EFFECTIVE Travel/Local classification.
 *
 * ⚠️ A sub-project's own `travel_job` is meaningless and must never be
 * shown. createSubProject inserts children without it and the column is NOT
 * NULL DEFAULT 0, so every child carries a 0 — while every read that matters
 * resolves travel status through the parent
 * (`COALESCE(pp.travel_job, p.travel_job)`). Rendering the child's own bit
 * put "Local" under a travel maintenance job whose crew the Gap Analysis tab
 * was counting as travel: a screen contradicting itself about the one fact
 * this column exists to state. Resolved here the same way the API resolves
 * it, so both answer alike.
 *
 * ⚠️ THE RESOLUTION HAPPENS IN SQL, not here. Every row the grid can hold
 * already carries the effective value: the schedule feed's three queries
 * select `COALESCE(pp.travel_job, p.travel_job)`, and rows synthesized from
 * project-options are top-level only (that endpoint filters
 * `parent_project_id IS NULL`), so their own bit is the right one.
 *
 * This used to resolve the parent client-side by looking it up in
 * `mergedRows`, and that had a hole: a parent which is archived, Complete or
 * Canceled is filtered out of the project list the grid merges, while its
 * non-archived child keeps its schedule rows. The lookup then found nothing,
 * returned false, and rendered a travel job's crew as Local — disagreeing
 * with gap analysis, which had always resolved the same crew through the
 * parent in SQL (Codex on #489).
 *
 * Kept as a named function rather than inlined, because "which bit is the
 * right bit" is the question this column keeps getting wrong, and it should
 * have one answer with the reasoning attached.
 *
 * ⚠️ MODULE SCOPE, so ordering inside the component can never bite. It used to
 * live in the body and had to sit above the export useMemo, because a const
 * declared lower is in the temporal dead zone when that memo runs. Now that it
 * needs nothing from the component, that hazard is gone rather than managed.
 */
function travelJobOf(project: ProjectRow): boolean { return project.travelJob === true; }

/** How the export names the lens the grid is under — the toggle's own wording,
 *  spelled out, because the file is read away from the buttons. */
const SCOPE_LABELS: Record<ScheduleScope, string> = {
  sp: 'Self-Perform (every division except Fabrication)',
  all: 'All Divisions',
  fab: 'Fabrication only',
};

// ─── Local UI types ───────────────────────────────────────────────────────────

interface CellKey {
  projectId: number;
  workDate: string;
  phase: CrewPhase;
}

interface ShiftState {
  projectId: number;
  offsetDays: number;
}

/** How far ahead a job shift looks for crew to move. The grid only ever holds
 *  the visible window, so the shift re-reads the run from the per-project feed;
 *  this bounds that read. Three years matches the project-summary horizon and
 *  comfortably covers the longest maintenance job. */
const SHIFT_HORIZON_DAYS = 365 * 3;

// BULK_DRAFT_MAX_ENTRIES (lib/bulkCellWrites) mirrors @ArrayMaxSize on the
// API's BulkDraftEntriesDto. A shift stages two rows per crew day (the new day,
// and a zero on the one it left), so a very long run can reach the cap now that
// the move is no longer clipped to the visible window; a multi-cell edit (#589)
// can reach it on a wide selection. Both check it first, so the scheduler gets
// a straight answer instead of a 400 from a request we could see was over.

// ─── Survey Chip ──────────────────────────────────────────────────────────────

const SURVEY_CHIP_BASE: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  right: 2,
  zIndex: 2,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 14,
  height: 14,
  padding: '0 4px',
  fontSize: 9,
  fontWeight: 700,
  lineHeight: '14px',
  color: '#fff',
  borderRadius: 'var(--ew-r-sm)',
  cursor: 'pointer',
  letterSpacing: '0.04em',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
  userSelect: 'none',
};
const SURVEY_CHIP_SCHEDULED_BG = '#0D4C7C';
const SURVEY_CHIP_IN_PROGRESS_BG = '#d97706';

interface SurveyChipProps {
  surveys: ScheduledSurveyRequest[];
  onOpen: (requestId: number) => void;
}

function SurveyChip({ surveys, onOpen }: SurveyChipProps) {
  // Amber takes precedence: if any survey on this cell is In Progress, the
  // cell as a whole is "active right now" — that's the state we want to draw
  // attention to.
  const hasInProgress = surveys.some((s) => s.status === 'In Progress');
  const chipStyle: React.CSSProperties = {
    ...SURVEY_CHIP_BASE,
    background: hasInProgress ? SURVEY_CHIP_IN_PROGRESS_BG : SURVEY_CHIP_SCHEDULED_BG,
  };

  // Build the tooltip text — one line per visit when more than one exists.
  const tooltip = surveys
    .map((s) => {
      const when = s.scheduledDateTime
        ? new Date(s.scheduledDateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        : '(time TBD)';
      const requester = s.requestedByText ?? s.requestedBy?.displayName ?? 'Unknown';
      const pm = s.project?.primaryPm?.displayName;
      const tech = s.scheduledTechnicianText;
      const duration = s.duration ? ` · ${s.duration}` : '';
      const header = s.status === 'In Progress' ? 'Survey in progress' : 'Survey scheduled';
      return [
        `${header} · ${when}${duration}`,
        tech ? `Technician: ${tech}` : null,
        `Requested by: ${requester}`,
        pm ? `PM: ${pm}` : null,
        `(click to view request)`,
      ].filter(Boolean).join('\n');
    })
    .join('\n———\n');

  // Click stops propagation so the cell's "click to edit" handler doesn't also fire.
  // With multiple requests on the same cell we open the first one (rare in practice).
  return (
    <span
      style={chipStyle}
      title={tooltip}
      onClick={(e) => { e.stopPropagation(); onOpen(surveys[0].id); }}
    >
      S{surveys.length > 1 ? `·${surveys.length}` : ''}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ManpowerPage() {
  // ⚠️ The EASTERN business day, not the browser's (#477). Everything below
  // derives from this — the default grid position, the "today" column, the
  // shift-plan horizon and the clear-future cutoff — and the API buckets the
  // figures printed beside them the same way.
  const today = easternToday();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  // Which sub-view of the Manpower area is active, route-driven so links are
  // shareable: /manpower = the Crew Schedule grid (formerly "Forecast");
  // /manpower/leaders = the field-leader assignment table backed by mp_leaders.
  // Later views (Look-Ahead, Capacity Forecast, Productivity) become siblings.
  // /manpower/history = the company-wide crew-schedule audit stream (#350).
  // /manpower/requests = the manpower request inbox (#303 / #420).
  const viewMode: ViewMode = (() => {
    const path = location.pathname.replace(/\/+$/, '');
    if (path.endsWith('/leaders'))  return 'leaders';
    // /manpower/travel is where the roster used to live. Kept as an alias so a
    // bookmark lands on the page that now contains it rather than on the grid.
    if (path.endsWith('/travel'))   return 'gap';
    if (path.endsWith('/gap'))      return 'gap';
    if (path.endsWith('/history'))  return 'history';
    if (path.endsWith('/requests')) return 'requests';
    return 'schedule';
  })();
  const setViewMode = (mode: ViewMode) =>
    navigate({ pathname: mode === 'schedule' ? '/manpower' : `/manpower/${mode}`, search: params.toString() });

  // Open-request count for the Requests toggle badge (#303 slice 3). Cheap
  // (one COUNT), so it polls: a scheduler leaves this page open all day and the
  // badge is how a new ask reaches them without a Teams ping. Null label = no badge.
  const openCountQ = useQuery({
    queryKey: ['manpower', 'request-open-count'],
    queryFn: manpowerApi.openRequestCount,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const openBadge = openCountLabel(openCountQ.data?.open);

  // Scope (?scope=) is resolved further down — its default depends on the
  // viewer's role, which the schedule data hook reports.
  const scopeParam = params.get('scope');

  // URL-driven window: ?weeks=2|4|6|8|13 and ?start=YYYY-MM-DD (snapped to its
  // week's Sunday). Defaults (2 weeks, current week) reproduce the classic
  // view and keep the URL clean.
  const weeksParam = Number(params.get('weeks'));
  const weeks = (RANGE_PRESETS as readonly number[]).includes(weeksParam) ? weeksParam : 2;
  const daily = isDailyGranularity(weeks);
  const startParam = params.get('start');
  const rangeStart = useMemo(() => {
    if (startParam && /^\d{4}-\d{2}-\d{2}$/.test(startParam)) {
      const d = new Date(startParam + 'T00:00:00');
      if (!isNaN(d.getTime())) return getWeekSunday(d);
    }
    return getWeekSunday(easternToday());
  }, [startParam]);
  const setRange = (start: Date, w: number = weeks) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      const startYmd = localYmd(getWeekSunday(start));
      if (startYmd === localYmd(getWeekSunday(today))) next.delete('start');
      else next.set('start', startYmd);
      if (w === 2) next.delete('weeks');
      else next.set('weeks', String(w));
      return next;
    }, { replace: true });
  };

  // Sections the user has hidden via the chip toggles. Section keys:
  // `div-{id}` for each division, `div-none` for projects with no division,
  // `closed-won` for ClosedWon placeholders. The special `systac` key is a
  // cross-division row filter (SYSTAC jobs live inside their division
  // sections). Empty set = everything visible.
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(() => new Set());
  // Job search — isolates matching rows (job # or name substring). Totals are
  // computed over the filtered rows, so the Grand Total reflects the isolated view.
  const [search, setSearch] = useState('');
  // Row order within each division section, and an optional "just this PM's
  // jobs" filter. Both requested by PMs 2026-07-25. Sorting stays inside the
  // sections so division subtotals and the capacity headroom keep working.
  // Bulk crew setup: lay in a whole job at once, then fine-tune by cell.
  const [setupTarget, setSetupTarget] = useState<ProjectRow | null>(null);
  const [sortKey, setSortKey] = useState<RowSort>('jobNumber');
  const [pmFilter, setPmFilter] = useState<string>('');
  /**
   * Travel / Local row filter.
   *
   * ⚠️ Local state, like the PM filter and the section chips — deliberately NOT
   * a URL param the way `scope` is. `scope` is in the URL because the three
   * lenses are three different boards people bookmark and send each other; this
   * narrows the board you are already on, and a link that silently hid half the
   * company's jobs would be worse than one that did not remember.
   *
   * ⚠️ Filters ROWS, and the totals follow, because every total on this page is
   * computed from `activeSections`. That is the existing contract — the search
   * and PM filters already work this way and the subtotal/headroom rows say
   * "totals reflect this view" — so a Travel-only board reports Travel-only
   * capacity rather than the company's.
   */
  const [travelFilter, setTravelFilter] = useState<'all' | 'travel' | 'local'>('all');
  // "Only jobs whose planned start disagrees with their first booked crew day"
  // (#385) — a worklist toggle, not a section toggle. Off by default: the grid's
  // job is the schedule, and this reduces it to an exception list on demand.
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [showImport, setShowImport]   = useState(false);
  const [showSync, setShowSync]       = useState(false);
  const [showHelp, setShowHelp]       = useState(false);
  // Draft → publish edit mode: edits stage server-side (invisible to PMs)
  // until Published. Daily granularity only — weekly-peak cells are aggregates.
  const [editMode, setEditMode]       = useState(false);
  // Project id awaiting "remove from manpower" confirmation on its grid row.
  const [removeConfirm, setRemoveConfirm] = useState<number | null>(null);
  const [editingCell, setEditingCell] = useState<CellKey | null>(null);
  const [shiftState, setShiftState]   = useState<ShiftState | null>(null);
  const [notifications, setNotifications] = useState<string[]>([]);
  const [editValue, setEditValue]     = useState('');
  const [availableEdit, setAvailableEdit]   = useState(false);
  const [availableValue, setAvailableValue] = useState('');
  // Multi-cell selection (#589) — edit mode, daily columns only. See
  // lib/cellSelection.ts for the rules and SelectionBar for the controls.
  const [selection, setSelection] = useState<Selection | null>(null);
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [selValue, setSelValue] = useState('');
  const [bulkReport, setBulkReport] = useState<BulkReport | null>(null);
  /** Where a drag started; the selection only appears once it reaches a second cell. */
  const dragFrom = useRef<CellRef | null>(null);
  /** The cell shift-click extends from: the last cell clicked or typed into. */
  const lastCell = useRef<CellRef | null>(null);
  /** Shift-click selects; it must not also open the cell's editor. */
  const swallowClick = useRef(false);
  const selInputRef = useRef<HTMLInputElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const availRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // ── Window: columns (day or weekly-peak) over a full 7-day calendar ────────
  const columns = useMemo(() => buildColumns(rangeStart, weeks), [rangeStart, weeks]);
  const groups  = useMemo(() => weekGroups(columns), [columns]);
  // Every day in the window — the query span and the per-date totals basis,
  // regardless of column granularity.
  const dateStrs = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < weeks * 7; i++) out.push(localYmd(addDays(rangeStart, i)));
    return out;
  }, [rangeStart, weeks]);
  const fromParam = dateStrs[0];
  const toParam   = dateStrs[dateStrs.length - 1];
  const dateIndex = useMemo(() => new Map(dateStrs.map((ds, i) => [ds, i])), [dateStrs]);
  /** A per-date totals array projected onto a column: the day's value, or the
   *  week's PEAK day (peak-of-sums for totals — days don't add across a week). */
  const colValue = (totals: number[], col: GridColumn): number =>
    col.kind === 'day'
      ? (totals[dateIndex.get(col.dates[0]) ?? -1] ?? 0)
      : Math.max(0, ...col.dates.map((ds) => totals[dateIndex.get(ds) ?? -1] ?? 0));

  // ── Data (queries + merge + sections + totals) ─────────────────────────────
  const {
    isLoading, isError, isFetching, dataReady,
    surveysByCell,
    availableMp,
    availableMpKnown,
    canEdit,
    fabEditOnly,
    isAdmin,
    maintenanceDayTotal,
    childrenByParentId,
    sections,
    visibleSections,
    systacCount,
    systacHidden,
    divisionOptions,
    pmOptions,
  } = useScheduleData({
    fromParam, toParam, dateStrs, hiddenSections, draft: editMode,
    sortKey, pmFilter: pmFilter || null,
  });

  /** Scope (?scope=): which divisions the grid shows.
   *
   *  - `sp`  Self-Perform — every division EXCEPT fabrication.
   *  - `all` every division, fabrication included.
   *  - `fab` the Fabrication lens: fab jobs only, plus capacity-ladder totals.
   *
   *  Self-Perform is the default for most people: fabrication crews are shop
   *  labor and aren't available for other self-perform work, so carrying them
   *  in the headline number overstated the crew a scheduler could actually
   *  deploy (#260). All three views stay one click apart and each is
   *  addressable by URL.
   *
   *  A fab-scoped editor (FabManpowerEditor) defaults to the Fabrication lens
   *  instead — Self-Perform would open their page on a grid holding none of the
   *  jobs they're allowed to touch. Whichever default applies clears the param
   *  rather than writing it, so the clean URL means "my usual view". */
  const defaultScope: ScheduleScope = fabEditOnly ? 'fab' : 'sp';
  const scope: ScheduleScope = resolveScope(scopeParam, fabEditOnly);
  const setScope = (s: ScheduleScope) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (s === defaultScope) next.delete('scope'); else next.set('scope', s);
      return next;
    }, { replace: true });

  // Draft mutations + unpublished-changes indicator (editors only). Failures
  // surface in the notification strip — never silently.
  const drafts = useDraftEdits({
    enabled: canEdit,
    onError: (msg) => setNotifications((prev) => [msg, ...prev].slice(0, 3)),
  });

  // Edit mode requires edit rights and daily granularity.
  useEffect(() => {
    if (editMode && (!canEdit || !daily)) setEditMode(false);
  }, [editMode, canEdit, daily]);

  // ── Scope filter + scoped totals ───────────────────────────────────────────
  // The Fabrication lens keeps only fab-division projects (in whatever section
  // they live — Needs Scheduling included). Totals are computed over the scoped
  // rows so subtotals/grand total always match what's on screen.
  const searchNorm = search.trim().toLowerCase();
  /** Apply a row predicate section by section, keeping a maintenance sub
   *  whenever its parent survives. A sub carries no job number of its own and
   *  its title rarely repeats the parent's, so filtering them independently
   *  stranded the parent as a header with nothing under it — the same reason
   *  the PM filter keeps them (useScheduleData). */
  const filterSections = (secs: typeof visibleSections, keep: (p: ProjectRow) => boolean) =>
    secs
      .map((s) => {
        const matched = s.projects.filter(keep);
        const matchedIds = new Set(matched.map((p) => p.projectId));
        return {
          ...s,
          projects: s.projects.filter((p) =>
            matchedIds.has(p.projectId)
            || (p.parentProjectId != null && matchedIds.has(p.parentProjectId))),
        };
      })
      .filter((s) => s.projects.length > 0);

  /**
   * Every narrowing EXCEPT the Start ≠ Crew chip.
   *
   * Split out so the chip's count and the chip's filter run over the same rows.
   * Counted over `visibleSections` instead — which is only post-section-chip and
   * post-PM-filter — the badge read (48) on a Self-Perform board that showed 36
   * when you clicked it, because scope, Type and the search are all applied here
   * rather than there. A count that does not equal what pressing it produces is
   * worse than no count.
   */
  const sectionsBeforeMismatch = useMemo(() => {
    let secs = visibleSections;
    if (scope === 'fab') secs = filterSections(secs, (p) => isFabProject(p));
    // Self-Perform is the mirror image: fabrication out, everything else in.
    else if (scope === 'sp') secs = filterSections(secs, (p) => !isFabProject(p));
    // ⚠️ travelJobOf, never the raw `travelJob` bit — the same resolution the
    // Type column renders, so the filter and the badge can never disagree about
    // a sub-project (whose own bit is always 0; its parent's is the real one).
    if (travelFilter !== 'all') {
      secs = filterSections(secs, (p) => travelJobOf(p) === (travelFilter === 'travel'));
    }
    if (searchNorm) {
      secs = filterSections(secs, (p) =>
        p.jobNumber.toLowerCase().includes(searchNorm) ||
        (p.title ?? '').toLowerCase().includes(searchNorm));
    }
    return secs;
  }, [visibleSections, scope, travelFilter, searchNorm]);

  const hasStartMismatch = (p: ProjectRow) =>
    startMismatch(p.currentStartDate, p.firstCrewDate) != null;

  /**
   * ⚠️ ISOLATES rather than hides, unlike the division and SYSTAC chips beside
   * it. Measured on prod before this shipped: 48 of 91 live jobs carrying both a
   * plan and booked crew disagree, two thirds of them by more than a week.
   * Scattered across the grid that is half the rows going rose, which reads as a
   * rendering fault rather than a worklist — so the chip has to be able to reduce
   * the grid TO them, not just remove them from it.
   *
   * Applied last, after every other narrowing, which is what lets the count above
   * be taken over the same set.
   */
  const activeSections = useMemo(
    () => (mismatchOnly ? filterSections(sectionsBeforeMismatch, hasStartMismatch) : sectionsBeforeMismatch),
    [sectionsBeforeMismatch, mismatchOnly],
  );

  /**
   * How many JOBS on the current board disagree — the chip's number.
   *
   * ⚠️ Jobs, not rows, and on a maintenance job the two differ. A flagged parent
   * counts once; its schedule-only sub-projects carry no alignment dates of their
   * own and count nothing, but `filterSections` keeps them on screen so the
   * parent is not left as a header with nothing under it. So pressing a chip
   * reading (1) can render three rows. That is the right count — a sub-project is
   * context, not a second disagreement — and the rows are the right rows; only a
   * claim that the two numbers are equal would be wrong.
   */
  const mismatchCount = useMemo(
    () => sectionsBeforeMismatch.reduce((n, s) => n + s.projects.filter(hasStartMismatch).length, 0),
    [sectionsBeforeMismatch],
  );

  const rows = useMemo<ProjectRow[]>(() => activeSections.flatMap((s) => s.projects), [activeSections]);

  // Subs a filter left off the grid, per parent. Their parent's cells still
  // paint them, so the parent has to carry them in the total or the subtotal
  // disagrees with the row above it. Empty in the ordinary unfiltered view.
  const offGridChildren = useMemo(() => {
    const onGrid = new Set(rows.map((p) => p.projectId));
    const m = new Map<number, ProjectRow[]>();
    for (const p of rows) {
      if (!p.isMaintenance) continue;
      m.set(p.projectId, (childrenByParentId.get(p.projectId) ?? []).filter((c) => !onGrid.has(c.projectId)));
    }
    return m;
  }, [rows, childrenByParentId]);

  const rowTotal = (p: ProjectRow, ds: string) =>
    getTotalledCrewCount(p, ds, offGridChildren.get(p.projectId) ?? []);

  const sectionTotals = useMemo<Map<string, number[]>>(() => {
    const m = new Map<string, number[]>();
    for (const s of activeSections) {
      m.set(s.key, dateStrs.map((ds) => s.projects.reduce((sum, p) => sum + rowTotal(p, ds), 0)));
    }
    return m;
  }, [activeSections, dateStrs, offGridChildren]);

  const colTotals = useMemo(
    () => dateStrs.map((ds) => rows.reduce((sum, p) => sum + rowTotal(p, ds), 0)),
    [rows, dateStrs, offGridChildren],
  );

  // Fab-lens totals: per-day fab-phase vs install-phase crew across
  // fab-division rows (NULL-phase entries count as fabrication — same fallback
  // the crew rows render with, so the totals match the grid).
  const { fabTotals, installTotals } = useMemo(() => {
    const fab = dateStrs.map(() => 0);
    const inst = dateStrs.map(() => 0);
    for (const p of rows) {
      if (!isFabProject(p)) continue;
      // Same roll-up rule as the totals above: a maintenance parent's own
      // entries are invisible on the grid and already represented by its subs.
      if (p.isMaintenance) continue;
      for (const e of p.dailyEntries) {
        const i = dateIndex.get(String(e.workDate).slice(0, 10));
        if (i == null) continue;
        if ((e.phase ?? null) === 'installation') inst[i] += e.crewCount;
        else fab[i] += e.crewCount;
      }
      // A fab pursuit's bid forecast counts here too (#425), or this footer
      // would contradict the Grand Total sitting on the same screen. It lands
      // on FABRICATION only, matching isForecastRow: the bid gives one
      // whole-job figure and the grid draws it on the shop line, so splitting
      // or duplicating it across both phases would invent detail the bid never
      // contained. Days with anything booked on the job are skipped by
      // getPlannedCrewCount, so nothing is double-counted.
      const fc = systacForecast(p);
      if (!fc) continue;
      dateStrs.forEach((ds, i) => {
        if (getCrewCount(p, ds) > 0) return;
        fab[i] += forecastCrewOn(fc, ds) ?? 0;
      });
    }
    return { fabTotals: fab, installTotals: inst };
  }, [rows, dateStrs, dateIndex]);

  // ── Export (#384) ──────────────────────────────────────────────────────────
  // Built from the same arrays the grid draws from, projected through the same
  // colValue() — so the workbook is the screen, not a second opinion about it.
  // In particular the weekly-peak projection is NOT reimplemented here: at 6+
  // weeks a column is a peak day, and computing that a second way is how the
  // file and the page start disagreeing.

  /**
   * Which division chips are switched off, by label.
   *
   * ⚠️ Shared by the workbook and the printed header, because they answer the
   * same question — "is this the whole company?" — and a filter the artifact
   * does not name is one the reader cannot know about. A hidden chip is the
   * easiest to forget of all of them: unlike a search box it leaves no text on
   * screen, and it survives from earlier in the session.
   */
  const hiddenSectionLabels = useMemo(
    () => sections.filter((s) => hiddenSections.has(s.key)).map((s) => s.label),
    [sections, hiddenSections],
  );

  /** Any filter that removes jobs — so the printed header and the workbook
   *  agree about whether the totals are company-wide. */
  const isFiltered = Boolean(
    search.trim() || pmFilter || hiddenSectionLabels.length > 0 || systacHidden
    || travelFilter !== 'all' || mismatchOnly,
  );

  /**
   * What the empty grid blames, when it is empty.
   *
   * ⚠️ pmFilter is applied INSIDE useScheduleData, so it empties `sections`
   * itself rather than `activeSections`. That is exactly why a PM filter
   * matching nobody used to reach the "no entries for this period" wording and
   * claim the whole period was bare. The reasoning for the rest, and for naming
   * the scope even at its default, is on describeNarrowings.
   */
  const activeNarrowings = useMemo(
    () => describeNarrowings({
      search, pmFilter, travelFilter, scope, hiddenSectionLabels, systacHidden, mismatchOnly,
    }),
    [search, pmFilter, travelFilter, scope, hiddenSectionLabels, systacHidden, mismatchOnly],
  );

  const scheduleExport = useMemo<ScheduleExportInput>(() => ({
    columns: columns.map((c) => ({ kind: c.kind, label: `${c.labelTop} ${c.labelBottom}`.trim() })),
    rows: activeSections.flatMap((section) =>
      section.projects.flatMap((p) => {
        const flags = exportRowFlags(p, !!section.isNeedsScheduling, dateStrs, fmtDate);
        // ⚠️ ONE EXPORTED ROW PER CREW LINE, not per job — because that is what
        // the grid draws. A fabrication job renders two lines (shop + field)
        // and collapsing them lost the split entirely: a workbook taken from
        // the Fabrication lens, whose whole purpose is that split, could not
        // say which job needed shop staff and which needed installers, and the
        // Totals sheet only carries it company-wide.
        //
        // ⚠️ TWO DIFFERENT FIGURES, and the row wants the other one.
        // rowTotal is a row's CONTRIBUTION to the totals, and for a maintenance
        // parent that is deliberately 0 in the ordinary view — its children are
        // on the grid speaking for themselves, so counting the parent as well
        // would double the job (#296). But the parent's own cells display
        // maintenanceDayTotal, the read-only Σ of those children. Exporting the
        // contribution put zeros on a row the screen shows crew on.
        //
        // So: display value for the ROW, contribution for the totals below —
        // and the row says which of the two it is holding, in `crewLine`, so a
        // reader who sums the sheet cannot silently double-count a roll-up.
        return buildCrewRows(p).map((crewRow) => ({
          sectionLabel: section.label,
          jobNumber: p.jobNumber,
          title: p.title,
          crewLine: p.isMaintenance
            ? 'Roll-up of sub-projects — do not add'
            : crewRow.phase === 'fabrication' ? 'Fabrication (shop)'
            : crewRow.phase === 'installation' ? 'Installation (field)'
            : 'Crew',
          pm: p.pm ?? null,
          flags,
          // The grid's own resolution, not the row's raw bit — otherwise the
          // workbook prints "No" against a sub-project whose parent is a
          // travel job, disagreeing with both the screen it came from and the
          // Gap Analysis tab counting that crew as travel demand.
          travelJob: travelJobOf(p),
          crew: columns.map((c) => colValue(
            dateStrs.map((ds) => (p.isMaintenance
              ? maintenanceDayTotal(p.projectId, ds)
              : crewLineValue(crewRow, ds))),
            c,
          )),
        }));
      })),
    sectionTotals: activeSections.map((s) => ({
      label: s.label,
      totals: columns.map((c) => colValue(sectionTotals.get(s.key) ?? [], c)),
    })),
    grandTotals: columns.map((c) => colValue(colTotals, c)),
    availableMp,
    scopeLabel: SCOPE_LABELS[scope],
    rangeLabel: formatRangeLabel(rangeStart, addDays(rangeStart, weeks * 7 - 1)),
    search: search.trim() || null,
    pmFilter: pmFilter || null,
    travelFilter: travelFilter === 'all' ? null : travelFilter,
    hiddenSections: hiddenSectionLabels,
    systacHidden,
    mismatchOnly,
    draft: editMode,
    fab: scope === 'fab'
      ? {
          fabTotals: columns.map((c) => colValue(fabTotals, c)),
          installTotals: columns.map((c) => colValue(installTotals, c)),
        }
      : undefined,
  }), [columns, activeSections, sectionTotals, colTotals, availableMp, scope, rangeStart, weeks,
       search, pmFilter, travelFilter, hiddenSectionLabels, systacHidden, mismatchOnly, editMode, fabTotals, installTotals,
       dateStrs, offGridChildren, maintenanceDayTotal]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  // NOTE: all crew-cell writes go through the draft layer (useDraftEdits) —
  // Edit Schedule is the single editing path. The live upsert endpoint still
  // exists server-side but this page no longer calls it.
  const updateConfigMutation = useMutation({
    mutationFn: (value: string) => manpowerApi.updateConfig('totalAvailableMp', value),
    // All of them: Admin → Config and the Gap Analysis read this row too.
    onSuccess: () => invalidateManpowerConfigQueries(queryClient),
  });

  // ── Maintenance sub-projects & row flags ─────────────────────────────────────
  const invalidateSchedule = () =>
    queryClient.invalidateQueries({ queryKey: ['manpower-schedule'] });

  const [rowMenu, setRowMenu]       = useState<number | null>(null); // projectId with its tag menu open
  const [addingSubTo, setAddingSubTo] = useState<number | null>(null); // maintenance parent we're adding a sub to
  const [subName, setSubName]       = useState('');
  const [deleteSubConfirm, setDeleteSubConfirm] = useState<number | null>(null); // sub-project pending delete

  const createSubProjectMutation = useMutation({
    mutationFn: ({ parentId, name }: { parentId: number; name: string }) =>
      manpowerApi.createSubProject(parentId, name),
    onSuccess: () => { setAddingSubTo(null); setSubName(''); invalidateSchedule(); },
  });
  const deleteSubProjectMutation = useMutation({
    mutationFn: (id: number) => manpowerApi.deleteSubProject(id),
    onSuccess: (_d, id) => {
      invalidateSchedule();
      // A maintenance sub-project is a real `projects` row, so it counts toward
      // Today eligibility like any other. Narrow — it has to be your only live
      // assignment — but the list is only worth having if it is complete.
      void invalidateAssignmentQueries(queryClient, id);
      // Its crew rows roll up into the PARENT's MIN(work_date), so deleting the
      // child can move the parent's first booked day (#385). The parent id is not
      // in hand here, so this clears broadly.
      void invalidateStartAlignmentQueries(queryClient);
    },
  });
  const setMaintenanceMutation = useMutation({
    mutationFn: ({ projectId, isMaintenance }: { projectId: number; isMaintenance: boolean }) =>
      manpowerApi.setMaintenance(projectId, isMaintenance),
    onSuccess: (_d, { projectId }) => {
      invalidateSchedule();
      // ⚠️ The flag's crew query filters on `ep.is_maintenance = 0`, so flipping
      // this decides whether a job's EXISTING entries count toward its first
      // booked day at all — setMaintenance does not clear the rows it inherits
      // (#385, and see getProjectScheduleSummary on the same filter).
      void invalidateStartAlignmentQueries(queryClient, projectId);
    },
  });
  const setGcLoanMutation = useMutation({
    mutationFn: ({ projectId, isLoaned, loanFromDivisionId }: { projectId: number; isLoaned: boolean; loanFromDivisionId?: number | null }) =>
      manpowerApi.setGcLoan(projectId, isLoaned, loanFromDivisionId),
    onSuccess: invalidateSchedule,
  });
  /**
   * Travel / Local (#396).
   *
   * ⚠️ Also invalidates the GAP ANALYSIS, not just this grid. The flag decides
   * which half of that tab every job's crew and leaders land in, so a
   * reclassification that left it cached would have the manpower lead looking at a board
   * saying Local and a chart still counting the job as Travel — the exact
   * disagreement the single flag exists to prevent.
   */
  const setTravelJobMutation = useMutation({
    mutationFn: ({ projectId, travelJob }: { projectId: number; travelJob: boolean }) =>
      manpowerApi.setTravelJob(projectId, travelJob),
    // One shared list, not a local one. Three review rounds each found a
    // different cache missing from the hand-written version here, and each fix
    // left the Overview's copy of the same list still wrong — see
    // invalidateTravelJobQueries for what is in it and why.
    onSuccess: (_data, { projectId }) => { invalidateTravelJobQueries(queryClient, projectId); },
  });

  function toggleSection(key: string) {
    setHiddenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  // Every other way of moving the window empties the From/To fields: once the
  // reader steps away from the range they typed, the fields would otherwise go
  // on naming a range the grid no longer shows.
  const goBack    = () => { clearJump(); setRange(addDays(rangeStart, -7)); };
  const goForward = () => { clearJump(); setRange(addDays(rangeStart, 7)); };
  const goToday   = () => { clearJump(); setRange(getWeekSunday(today)); };
  const choosePreset = (w: number) => { clearJump(); setRange(rangeStart, w); };

  /**
   * Jump to a date or range (#588) — the Concrete Pour Schedule's date control,
   * translated for a grid that holds one fetched WINDOW rather than a list it
   * can filter. Rules and the reasons for them are in lib/gridJump.ts.
   *
   * Local state, like the PM filter: the window it produces IS in the URL
   * (?start / ?weeks), so a shared link still lands on the same weeks; only
   * the typed fields aren't carried.
   */
  const [jumpFrom, setJumpFrom] = useState('');
  const [jumpTo, setJumpTo]     = useState('');
  const [jumpCapped, setJumpCapped] = useState(false);
  function clearJump() { setJumpFrom(''); setJumpTo(''); setJumpCapped(false); }
  function applyJump(from: string, to: string) {
    setJumpFrom(from);
    setJumpTo(to);
    // `weeks` is already narrowed to a preset where it is read off the URL.
    const w = windowFor(from, to, weeks as RangeWeeks);
    // Null = nothing usable yet (a half-typed year, or both fields cleared):
    // the window stays put rather than fetching somewhere nobody asked for.
    if (!w) { setJumpCapped(false); return; }
    setJumpCapped(w.capped);
    setRange(parseYmd(w.start)!, w.weeks);
  }

  // ── Multi-cell selection (#589) ────────────────────────────────────────────
  // Drag across day cells, or click one and shift-click another, then Set /
  // Clear / Copy / Paste. Everything stages through the same bulk draft route
  // as the stepper and ⇄ Shift, so Publish makes it live and Reset undoes it.
  // Rules are in lib/cellSelection.ts, lib/bulkCellWrites.ts and
  // lib/crewClipboard.ts, each with a spec.
  //
  // Edit mode and daily columns only: a weekly column is a read-only peak, and
  // outside edit mode nothing on the board is writable.
  const selectable = editMode && canEdit && daily;
  const todayYmd = localYmd(today);
  /** Crew lines in screen order — what a selection rectangle and a paste walk. */
  const crewRowsOnScreen = useMemo(
    () => activeSections.flatMap((s) => s.projects.flatMap(buildCrewRows)),
    [activeSections],
  );
  const selRowKeys = useMemo(() => crewRowsOnScreen.map((r) => r.key), [crewRowsOnScreen]);
  const selRowsByKey = useMemo(() => new Map(crewRowsOnScreen.map((r) => [r.key, r])), [crewRowsOnScreen]);
  const refusalCtx = useMemo(() => ({ fabEditOnly, today: todayYmd }), [fabEditOnly, todayYmd]);
  const activeSelection = selectable ? selection : null;

  /** What the selection covers, and how each covered cell will be treated —
   *  drawn on the grid and summarized in the bar BEFORE anything is applied. */
  const selInfo = useMemo(() => {
    if (!activeSelection) return null;
    const b = selectionBounds(activeSelection, selRowKeys, dateStrs);
    if (!b) return null;
    const cells = selectedCells(activeSelection, selRowKeys, dateStrs, skipWeekends);
    const { allowed, refused } = partitionWrites(cells.map((c) => ({ ...c, value: 0 })), selRowsByKey, refusalCtx);
    const marks = new Map<string, Refusal | 'on'>();
    for (const a of allowed) marks.set(`${a.rowKey}|${a.ds}`, 'on');
    for (const r of refused) marks.set(`${r.rowKey}|${r.ds}`, r.reason);
    return {
      b,
      rows: b.r1 - b.r0 + 1,
      days: selectionDays(dateStrs, b.c0, b.c1, skipWeekends).length,
      writable: allowed.length,
      refusedCount: refused.length,
      refused: describeRefusals(refused),
      weekendsSkipped: skipWeekends ? skippedWeekendCount(activeSelection, selRowKeys, dateStrs) : 0,
      marks,
    };
  }, [activeSelection, selRowKeys, selRowsByKey, dateStrs, skipWeekends, refusalCtx]);

  // A selection belongs to the window, the mode AND the rows it was drawn over.
  // A filter that hides an end row would otherwise leave it armed but
  // invisible, and clearing a filter would silently widen the rectangle over
  // rows that weren't on screen when it was drawn.
  const rowSetKey = useMemo(() => selRowKeys.join('|'), [selRowKeys]);
  useEffect(() => { setSelection(null); }, [fromParam, toParam, editMode, rowSetKey]);
  useEffect(() => { if (!editMode) setBulkReport(null); }, [editMode]);

  /**
   * Stage a set / clear / paste. Refused cells are split off and REPORTED, never
   * dropped quietly; the rest go out as one bulk draft, so the server's
   * all-or-nothing checks still stand behind the client's.
   */
  function runBulk(kind: 'set' | 'clear' | 'paste', writes: CellWrite[], notes: string[], value?: number) {
    if (!selectable) return;
    // Never drop a keystroke or a paste without saying so. The refetch after a
    // save counts too: until it lands, the cells on screen are the figures from
    // BEFORE the last change, and planning against them would skip "no-op"
    // cells that are no longer what the grid shows.
    if (drafts.busy || isFetching) {
      // isFetching alone is also the background refresh and a window change,
      // where "still saving your change" would be untrue.
      setBulkReport({
        title: drafts.busy
          ? 'Still saving your last change — nothing was changed. Try that again in a moment.'
          : 'The grid is still loading the latest figures — nothing was changed. Try that again in a moment.',
        notes: [], refused: [], failed: true,
      });
      return;
    }
    if (writes.length === 0) {
      setBulkReport({
        title: skipWeekends && selInfo?.days === 0
          ? 'Nothing to change — the selection is all Saturday/Sunday and Skip Sat/Sun is on. Untick it to include weekends.'
          : 'Nothing to change — no selected cell is in the window.',
        notes, refused: [], failed: true,
      });
      return;
    }
    const { allowed, refused: refusedAll } = partitionWrites(writes, selRowsByKey, refusalCtx);
    // Report only refusals that BLOCKED a change. A refused cell already
    // holding the figure (a past day in a block pasted back over itself) was
    // never going to change, and listing it reads as if something was stopped.
    const refused = refusedAll.filter((r) => getPhaseCellMeta(selRowsByKey.get(r.rowKey)!, r.ds).count !== r.value);
    const plan = planBulkWrites(allowed, dateStrs);
    const refusedLines = describeRefusals(refused);
    if (plan.entries.length > BULK_DRAFT_MAX_ENTRIES) {
      setBulkReport({
        title: `That would stage ${plan.entries.length} cells — more than the ${BULK_DRAFT_MAX_ENTRIES} one change can carry. Nothing was changed; select fewer lines or days.`,
        notes: [], refused: [], failed: true,
      });
      return;
    }
    if (plan.entries.length === 0) {
      setBulkReport({
        title: refused.length > 0
          ? 'Nothing was changed — every cell it would have written is listed below.'
          : 'Nothing to change — those cells already hold that.',
        notes, refused: refusedLines, failed: refused.length > 0,
      });
      return;
    }
    const n = plan.changed;
    const cells = `${n} cell${n === 1 ? '' : 's'}`;
    const r = plan.restaged;
    const restagedNote = `${r} cell${r === 1 ? '' : 's'} already showing that figure ${r === 1 ? 'was' : 'were'} staged anyway — a staged change now in your draft, or a Fabrication day moved off its old unphased entry.`;
    // A batch that changes no figure but still stages cells must say so, not
    // report "0 cells" beside a draft count that just went up.
    const what = n === 0 ? 'No figures changed'
      : kind === 'set' ? `Set ${cells} to ${value}` : kind === 'clear' ? `Cleared ${cells}` : `Pasted — ${cells} changed`;
    const extra = [
      ...(kind !== 'clear' && plan.replaced > 0 ? [`${plan.replaced} of them already had crew, now replaced.`] : []),
      ...(r > 0 ? [restagedNote] : []),
    ];
    // The last action's report must not sit there looking like this one's
    // while it is in flight — if it fails, the failure notice stands alone.
    setBulkReport(null);
    drafts.bulk.mutate(plan.entries, {
      onSuccess: () => setBulkReport({
        title: `${what} — staged, publish to make it live. Reset changes undoes it, along with the rest of your draft.`,
        notes: [...extra, ...notes],
        refused: refusedLines,
      }),
    });
  }

  const weekendNote = (): string[] =>
    selInfo && selInfo.weekendsSkipped > 0
      ? [`${selInfo.weekendsSkipped} Saturday/Sunday cell${selInfo.weekendsSkipped === 1 ? '' : 's'} left alone (Skip Sat/Sun is on).`]
      : [];

  function selSet(value: number) {
    if (!activeSelection) return;
    runBulk('set', selectedCells(activeSelection, selRowKeys, dateStrs, skipWeekends).map((c) => ({ ...c, value })), weekendNote(), value);
  }

  function selClear() {
    if (!activeSelection) return;
    runBulk('clear', selectedCells(activeSelection, selRowKeys, dateStrs, skipWeekends).map((c) => ({ ...c, value: 0 })), weekendNote());
  }

  /** The selection as tab-separated text. Booked crew only — see copyBlock. */
  function selCopyText(): string | null {
    if (!selInfo) return null;
    // A Sat/Sun-only selection with Skip Sat/Sun on has no days to copy.
    // Putting one blank line per row on the clipboard would paste later as
    // zeros and CLEAR crew, so copy nothing and say why.
    if (selInfo.days === 0) {
      setBulkReport({ title: 'Nothing copied — the selection is all Saturday/Sunday and Skip Sat/Sun is on. Untick it to copy weekends.', notes: [], refused: [], failed: true });
      return null;
    }
    const { b } = selInfo;
    return toClipboardText(copyBlock(crewRowsOnScreen.slice(b.r0, b.r1 + 1), selectionDays(dateStrs, b.c0, b.c1, skipWeekends)));
  }

  function selCopy() {
    const text = selCopyText();
    if (text == null) return;
    // The async Clipboard API is missing outside a secure origin; Ctrl+C (the
    // copy event below) still works there.
    if (!navigator.clipboard) {
      setBulkReport({ title: 'This browser won’t let the button copy — press Ctrl+C instead.', notes: [], refused: [], failed: true });
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => setBulkReport({ title: `Copied ${selInfo!.rows} × ${selInfo!.days} — paste it on the grid or into Excel.`, notes: [], refused: [] }),
      () => setBulkReport({ title: 'The browser wouldn’t allow copying from the button — press Ctrl+C instead.', notes: [], refused: [], failed: true }),
    );
  }

  /** Paste clipboard text from the top-left of `at`. */
  function pasteText(text: string, at: Selection) {
    const parsed = parseClipboard(text);
    if (!parsed.ok) {
      setBulkReport({ title: parsed.message, notes: [], refused: [], failed: true });
      return;
    }
    const p = placeBlock({ matrix: parsed.matrix, sel: at, rowKeys: selRowKeys, days: dateStrs, skipWeekends });
    const notes: string[] = [];
    if (p.clippedRows > 0) {
      notes.push(`${p.clippedRows} clipboard row${p.clippedRows === 1 ? '' : 's'} ran past the last line on screen and ${p.clippedRows === 1 ? 'wasn’t' : 'weren’t'} pasted.`);
    }
    if (p.clippedCols > 0) {
      notes.push(`${p.clippedCols} clipboard column${p.clippedCols === 1 ? '' : 's'} ran past ${fmtDate(toParam)} and ${p.clippedCols === 1 ? 'wasn’t' : 'weren’t'} pasted — move the window on and paste the rest.`);
    }
    const dates = p.writes.map((w) => w.ds).sort();
    if (skipWeekends && dates.length > 1
      && dateStrs.some((d) => d > dates[0] && d < dates[dates.length - 1] && isWeekendYmd(d))) {
      notes.push('Pasted onto weekdays only — Saturday and Sunday were stepped over (Skip Sat/Sun is on).');
    }
    runBulk('paste', p.writes, notes);
  }

  /** Mouse: press starts a drag; reaching a second cell makes it a selection.
   *  Shift+press extends from the last cell clicked. A plain click still opens
   *  the cell for typing — the existing gesture is unchanged. */
  function cellMouseDown(e: React.MouseEvent, cell: CellRef) {
    if (!selectable || e.button !== 0) return;
    if (e.shiftKey) {
      e.preventDefault();
      // Commits a cell that is open for typing (through its onBlur) before
      // the selection replaces it.
      (document.activeElement as HTMLElement | null)?.blur?.();
      setEditingCell(null);
      // The last cell clicked may have left the screen since (the window
      // moved, or a filter hid its row); extending from it would select
      // nothing. Start from here instead.
      const from = lastCell.current;
      const anchor = from && selRowKeys.includes(from.rowKey) && dateStrs.includes(from.ds) ? from : cell;
      setSelection({ anchor, focus: cell });
      lastCell.current = anchor;
      swallowClick.current = true;
      return;
    }
    // Every click starts with a press, so this is where a swallow left over
    // from a shift-click that produced no click (on a maintenance parent's
    // cell, or an open input) is let go — else it would eat this click.
    swallowClick.current = false;
    dragFrom.current = cell;
    lastCell.current = cell;
  }
  function cellMouseEnter(e: React.MouseEvent, cell: CellRef) {
    const from = dragFrom.current;
    if (!from || !(e.buttons & 1)) return;
    // A drag that starts INSIDE an open cell doesn't blur it (the press never
    // left the input), so commit it through its onBlur before the selection
    // unmounts it — otherwise the typed figure is lost.
    if (editingCell) (document.activeElement as HTMLElement | null)?.blur?.();
    setEditingCell(null);
    setSelection({ anchor: from, focus: cell });
    // A drag that wanders out and back ends on the cell it began on, where the
    // browser DOES send a click — which would open the editor and throw the
    // selection away. The next press clears this (cellMouseDown).
    swallowClick.current = true;
  }
  useEffect(() => {
    const up = () => { dragFrom.current = null; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  /** A cell open for typing that a block was pasted into: its own commit must
   *  not then race the paste and write the old figure back over it. */
  const abandonedEdit = useRef<string | null>(null);

  // Keyboard and clipboard while a selection is up. Read through a ref so the
  // document listeners are attached once rather than on every render.
  // Only a selection that is actually drawn arms the keys.
  const armedSelection = selInfo ? activeSelection : null;
  const selKeys = useRef({ selection: armedSelection, editing: editingCell != null, selClear, pasteText, selCopyText });
  selKeys.current = { selection: armedSelection, editing: editingCell != null, selClear, pasteText, selCopyText };
  useEffect(() => {
    // Fields that take typing or a paste of their own. A checkbox or button
    // is not one: after toggling Skip Sat/Sun, focus stays on the checkbox,
    // and Delete / Ctrl+V must still reach the selection.
    const inField = (t: EventTarget | null) =>
      t instanceof HTMLElement && !!t.closest(
        'textarea, select, [contenteditable="true"], input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])',
      );
    // Where the keys belong to the selection: nothing focused (after a click
    // on a cell, focus is the body), the grid, the selection bar, or the
    // report under it. NOT a dialog opened over the grid (Set up, Import,
    // Help) — a Backspace on one of its buttons must not clear cells behind it.
    const inScope = (t: EventTarget | null) =>
      t === document.body || t === document.documentElement
      || (t instanceof HTMLElement && !!t.closest('.mp-scroll, .mp-sel, .mp-bulk'));
    const onKey = (e: KeyboardEvent) => {
      const s = selKeys.current;
      if (!s.selection || s.editing || !inScope(e.target) || inField(e.target)) return;
      if (e.key === 'Escape') { setSelection(null); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); s.selClear(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        // Cut-and-paste onto other dates is a MOVE of crew in time, and that
        // already has a tool that says what a move does not do: ⇄ Shift, whose
        // notice warns that the job's phase dates stay put (#456). A second
        // move mechanism that skips the warning is what this declines to be.
        setBulkReport({
          title: 'Cut isn’t offered on the crew grid.',
          notes: ['To move a job’s crew to other dates, use ⇄ Shift on the job — it moves the whole run and reminds you the phase dates stay where they are. Copy and Clear are here for everything else.'],
          refused: [], failed: true,
        });
        return;
      }
      if (/^[0-9]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setSelValue(e.key);
        selInputRef.current?.focus();
      }
    };
    const onCopy = (e: ClipboardEvent) => {
      const s = selKeys.current;
      if (!s.selection || s.editing || !inScope(e.target) || inField(e.target)) return;
      // Text the reader highlighted on the page (a job name, a notice) is
      // what they meant to copy, not the grid selection. Grid cells are
      // user-select: none, so a drag over them never leaves a text selection.
      const highlighted = window.getSelection();
      if (highlighted && !highlighted.isCollapsed && highlighted.toString().trim() !== '') return;
      const text = s.selCopyText();
      if (text == null) return;
      e.preventDefault();
      e.clipboardData?.setData('text/plain', text);
    };
    const onPaste = (e: ClipboardEvent) => {
      const s = selKeys.current;
      if (!s.selection || s.editing || !inScope(e.target)) return;
      const text = e.clipboardData?.getData('text/plain') ?? '';
      // The Set-to box takes a single number natively; a BLOCK pasted there is
      // meant for the grid. Any other field keeps its own paste.
      if (inField(e.target)
        && !((e.target as HTMLElement).dataset?.gridPaste && isBlockPaste(text))) return;
      e.preventDefault();
      s.pasteText(text, s.selection);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('paste', onPaste);
    };
  }, []);

  /** Selection overlay for one day cell, and the mouse handlers that draw it. */
  function selCellProps(crewRow: CrewRow, ds: string): {
    style: React.CSSProperties; title?: string;
    onMouseDown?: (e: React.MouseEvent) => void; onMouseEnter?: (e: React.MouseEvent) => void;
  } {
    if (!selectable) return { style: {} };
    const cell = { rowKey: crewRow.key, ds };
    const mark = selInfo?.marks.get(`${crewRow.key}|${ds}`);
    const style: React.CSSProperties = { userSelect: 'none' };
    let title: string | undefined;
    if (mark === 'on') {
      style.boxShadow = 'inset 0 0 0 2px var(--ew-quality), inset 0 0 0 9999px rgba(24, 117, 189, 0.16)';
    } else if (mark) {
      // Refused: hatched, and the tooltip says why — visible before Apply.
      style.backgroundImage = 'repeating-linear-gradient(45deg, rgba(184, 50, 28, 0.22) 0 3px, transparent 3px 7px)';
      style.boxShadow = 'inset 0 0 0 1px var(--ew-danger)';
      title = `Selected, but won’t change: ${REFUSAL_TEXT[mark]}`;
    }
    return {
      style, title,
      onMouseDown: (e) => cellMouseDown(e, cell),
      onMouseEnter: (e) => cellMouseEnter(e, cell),
    };
  }

  // ── Cell editing (Edit Schedule mode only — the single editing path) ───────

  /** Row-level scope for fab-only editors (FabManpowerEditor): they may edit
   *  Fabrication-division jobs only. The server enforces this on every draft
   *  write; this keeps the UI honest so out-of-scope cells never look editable.
   *  The rule itself is `outsideFabScope`, shared with the cells and the
   *  multi-cell path (#589). */
  const rowEditable = (p: Parameters<typeof isFabProject>[0]) => !outsideFabScope(p, fabEditOnly);

  function startEdit(crewRow: CrewRow, ds: string, current: number) {
    if (!canEdit || !editMode) return;
    // Typing into one cell ends any multi-cell selection (#589), and is the
    // cell a later shift-click extends from.
    setSelection(null);
    lastCell.current = { rowKey: crewRow.key, ds };
    abandonedEdit.current = null;
    setEditingCell({ projectId: crewRow.projectId, workDate: ds, phase: crewRow.phase });
    editTouched.current = false;
    setEditValue(current === 0 ? '' : String(current));
  }
  /** Whether the open cell has had a keystroke or paste — see commitEdit. */
  const editTouched = useRef(false);

  function commitEdit(row: CrewRow, ds: string) {
    setEditingCell(null);
    if (!editMode) return;
    if (abandonedEdit.current === `${row.key}|${ds}`) { abandonedEdit.current = null; return; }
    // The same validation as paste and the bar's Set box (#589), so a figure
    // is not refused in bulk and accepted typed — and a bad one says so
    // instead of being dropped without a word.
    //
    // Only for a figure the scheduler actually TYPED. A cell opened and left
    // untouched commits what it already held, exactly as before — a value over
    // 200 from before this check (the API takes up to 999) must not read
    // "Not saved" just for being clicked.
    const parsed = parseCrewValue(editValue);
    const untouched = !editTouched.current;
    if (!parsed.ok && !untouched) {
      setNotifications((prev) => [`Not saved — “${editValue}” on ${rowJobLabel(row, jobNumbersById(crewRowsOnScreen))}, ${fmtDate(ds)}: ${parsed.why}.`, ...prev].slice(0, 3));
      return;
    }
    const n = parsed.ok ? parsed.value : Number(editValue);
    // The rules (pin on a type-in that differs from the line's rate, pin kept
    // for an untouched cell, the fab legacy zero in the same batch) are
    // planTypedCell's, so they are tested.
    const entries = planTypedCell(row, ds, n, dateStrs, untouched);
    if (entries.length === 1) drafts.upsert.mutate(entries[0]);
    else if (entries.length > 1) drafts.bulk.mutate(entries);
  }

  /** Stepper: set the row's base headcount across its run (phase span when
   *  known), staged as a draft. Day-level overrides are left alone. */
  function stepRow(row: CrewRow, delta: number) {
    if (!editMode) return;
    const targets = stepperTargetDays(row, dateStrs);
    if (targets.length === 0) return;
    const next = Math.max(0, stepperValue(row, targets) + delta);
    const entries: DraftEntryInput[] = targets.map((ds) => ({
      workDate: ds, projectId: row.projectId, crewCount: next,
      phase: row.phase, isOverride: false,
    }));
    // Fabrication line: migrate any legacy NULL-phase days in the run so the
    // new phased values don't double-count on top of them.
    if (row.phase === 'fabrication') {
      for (const ds of targets) {
        if (nullPhaseEntryFor(row.project, ds)) {
          entries.push({ workDate: ds, projectId: row.projectId, crewCount: 0, phase: null, isOverride: false });
        }
      }
    }
    drafts.bulk.mutate(entries);
  }

  /** Edit-mode crew stepper (− N +): sets a crew line's base headcount across
   *  its run in one staged bulk edit. Day-level overrides are preserved. */
  function crewStepper(crewRow: CrewRow) {
    if (!editMode || crewRow.project.isMaintenance || !rowEditable(crewRow.project)) return null;
    const targets = stepperTargetDays(crewRow, dateStrs);
    const val = stepperValue(crewRow, targets);
    return (
      <span
        // The − / + buttons go on their own under the print rule, but the value
        // between them would be left behind as a bare number beside the job
        // name — so the whole control leaves together.
        className="mp-noprint"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8 }}
        title={`Set this crew line across its run (${targets.length} weekday${targets.length === 1 ? '' : 's'} in view · day overrides kept)`}
      >
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '1px 8px', lineHeight: 1.4 }} disabled={drafts.busy || (val <= 0 && targets.length === 0)} onClick={() => stepRow(crewRow, -1)} aria-label="Decrease crew across the run">−</button>
        <span style={{ fontSize: 12, fontWeight: 700, minWidth: 18, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{val}</span>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '1px 8px', lineHeight: 1.4 }} disabled={drafts.busy} onClick={() => stepRow(crewRow, +1)} aria-label="Increase crew across the run">+</button>
      </span>
    );
  }

  /** Move a job's crew run by N days, from today forward.
   *
   *  The run is re-read from the per-project feed rather than taken from
   *  `project.dailyEntries`, which only ever holds the visible window: sourcing
   *  the move off the grid shifted the weeks on screen and stranded the rest,
   *  so a delayed job's start moved but its tail stayed put (#286). Past weeks
   *  are left alone — they're booked history, the same line "Clear future crew"
   *  draws. A negative offset can still pull crew back before today, which is
   *  what pulling a job earlier means. */
  async function shiftProjectRow(project: ProjectRow) {
    if (!shiftState || shiftState.projectId !== project.projectId || !canEdit || !editMode) return;
    const offset = shiftState.offsetDays;
    if (offset === 0) { setShiftState(null); return; }

    // Read wide enough to cover both ends of the move: the source run (today →
    // horizon) plus wherever the offset lands it, so a backward shift can still
    // see the days it would land on.
    const todayYmd = localYmd(today);
    const fetchFrom = localYmd(addDays(today, Math.min(offset, 0)));
    const fetchTo   = localYmd(addDays(today, SHIFT_HORIZON_DAYS + Math.max(offset, 0)));

    let crewDays: { workDate: string; phase: string | null; crewCount: number; isOverride: boolean }[];
    try {
      crewDays = await manpowerApi.projectCrewDays(project.projectId, fetchFrom, fetchTo, true);
    } catch {
      setNotifications((prev) => [`Could not load ${project.jobNumber}'s crew run — shift cancelled, nothing was staged.`, ...prev].slice(0, 3));
      return;
    }

    // Where the run lands, what it vacates, and what it would overwrite getting
    // there. Pure, and specced in lib/shiftPlan.spec.ts: #452 and #286 were both
    // filed against this arithmetic, and neither could be answered without
    // reading production, because none of it was pinned.
    const plan = planShiftedRun({
      projectId: project.projectId, crewDays, today: todayYmd, offset,
    });
    if (plan.movedDays === 0) {
      setNotifications((prev) => [`${project.jobNumber} has no crew booked from today onward — nothing to shift.`, ...prev].slice(0, 3));
      setShiftState(null);
      return;
    }

    // Stage the whole move as one bulk draft. Landing on days that already
    // carry crew overwrites them — recoverable via Reset, and confirmed first.
    // Only a BACKWARD shift can reach such a day (a forward one only ever lands
    // inside its own run or past its end), so this prompt guards booked history.
    if (plan.collisions > 0 && !confirm(`Shifting lands on ${plan.collisions} day(s) that already have crew — overwrite them? (Reset changes can undo this before publish.)`)) {
      return;
    }
    if (plan.entries.length > BULK_DRAFT_MAX_ENTRIES) {
      setNotifications((prev) => [`${project.jobNumber} has ${plan.movedDays} crew days booked from today on — too many to shift in one move. Shift it in stages, or clear and rebook the tail.`, ...prev].slice(0, 3));
      return;
    }
    drafts.bulk.mutate(plan.entries);
    setShiftState(null);
    // ⚠️ The notice SAYS THE PHASE DATES DID NOT MOVE, and that sentence is the
    // whole of #456. This button moves crew only — it cannot touch phase dates,
    // which are writable solely through PATCH /projects/:id/phases, a route this
    // module never calls. Staying quiet about them is what made #452/#453 read
    // as a shift bug: a week of crew moved, nothing mentioned the schedule
    // dates, and the dates were then re-typed by hand with a day lost.
    // Wording lives in lib/shiftNotice.ts, with a spec.
    setNotifications((prev) => [
      shiftNotice({
        jobNumber: project.jobNumber,
        offsetDays: offset,
        movedDays: plan.movedDays,
        // A schedule-only sub-project is excluded from the Projects list
        // (`parent_project_id IS NULL`), so it has no card to send anyone to.
        hasProjectPage: project.parentProjectId == null,
      }),
      ...prev,
    ].slice(0, 3));
  }

  function handleCellKey(e: React.KeyboardEvent, row: CrewRow, ds: string) {
    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); commitEdit(row, ds); }
    else if (e.key === 'Escape') setEditingCell(null);
  }

  /** The day/week cells for one crew line. Weekly columns are read-only
   *  peaks; day cells are click-to-edit (or batch inputs). Weekends shade
   *  when empty but stay editable (weekend work happens). A crew line's
   *  phase span (from project_phases) tints its empty in-window cells so the
   *  scheduler sees the run the crew number applies to. */
  function renderCrewCells(crewRow: CrewRow) {
    const project = crewRow.project;
    // The same predicate the multi-cell path builds on (lib/cellSelection), so
    // a bulk edit can never reach a cell this click refuses (#589).
    const rowEd = singleCellRefusal(crewRow, { fabEditOnly }) === null;
    // Bid forecast for a SYSTAC pursuit (#330). Read-only, and only ever shown
    // in cells with no scheduled crew — an entered number is the plan, and a
    // bid estimate must never sit on top of it.
    //
    // ⚠️ ONE LINE PER PROJECT. A fab job has two crew lines but one whole-job
    // bid figure, so this is null on the second line — otherwise an empty fab
    // pursuit showed the forecast twice, reading as double the crew.
    // Suppression is likewise judged on the PROJECT's crew for the day, not
    // this line's: crew booked against fabrication must silence the forecast
    // on installation too.
    const forecast = isForecastRow(crewRow) ? systacForecast(project) : null;

    /** What this crew line plans for one day: booked crew, else the bid figure.
     *  Mirrors the day cell exactly — the weekly peak is built from these, so a
     *  week column can never contradict the days inside it. */
    const weekDayPlanned = (row: CrewRow, d: string): { crew: number; fromBid: boolean } => {
      const booked = getPhaseCrewCount(row, d);
      if (booked > 0) return { crew: booked, fromBid: false };
      // Crew booked on the job's OTHER line still silences the bid figure —
      // the bid is one whole-job number, not one per phase.
      if (getCrewCount(project, d) > 0) return { crew: 0, fromBid: false };
      const bid = forecastCrewOn(forecast, d) ?? 0;
      return { crew: bid, fromBid: bid > 0 };
    };

    return columns.map((col, ci) => {
      const weekDivider = col.isWeekStart && ci > 0 ? '2px solid var(--ew-n-300)' : undefined;

      // Weekly column: read-only PEAK crew/day across the week (aggregates
      // are not editable — switch to a 2/4-wk range to edit individual days).
      if (col.kind === 'week') {
        const peak = project.isMaintenance
          ? Math.max(0, ...col.dates.map((d) => maintenanceDayTotal(project.projectId, d)))
          : Math.max(0, ...col.dates.map((d) => getPhaseCrewCount(crewRow, d)));
        // Peak of the week's PER-DAY planned crew — the same figure the day
        // cells draw, so the weekly view, the day view and the footer all agree.
        //
        // ⚠️ Resolved per day, never per week. Choosing booked-or-forecast for
        // the whole week made one booked Monday suppress the bid figure across
        // the other six days: the row peaked at Monday's 3 while the totals,
        // which resolve each day on its own, peaked at the forecast 7 (Codex on
        // #425). The row has to be able to say "3 booked, 7 anticipated later
        // that week" — and the peak of those days is 7.
        const wDays = col.dates.map((d) => weekDayPlanned(crewRow, d));
        const wTop = wDays.reduce((a, b) => (b.crew > a.crew ? b : a), { crew: 0, fromBid: false });
        const wShown = project.isMaintenance ? peak : wTop.crew;
        const wfill = crewFillStyle(wShown);
        // Same subtotal treatment as the day cells: a maintenance parent's
        // figure is the sum of the rows below it, not crew booked on the job.
        return (
          <td key={ci} style={{
            ...TD, padding: '5px 3px', textAlign: 'center',
            borderLeft: weekDivider,
            background: project.isMaintenance ? '#f8fafc' : (wShown > 0 ? (wfill.background as string | undefined) : undefined),
            color: project.isMaintenance
              ? (peak > 0 ? 'var(--ew)' : 'transparent')
              : wShown > 0 ? (wfill.color as string | undefined)
              : 'transparent',
            fontWeight: project.isMaintenance ? 600 : 700,
            fontStyle: project.isMaintenance ? 'italic' : undefined,
            fontSize: 12, fontVariantNumeric: 'tabular-nums',
          }} title={project.isMaintenance
            ? "Weekly peak of this job's sub-project subtotal — counted once in the grand total, through the sub-project rows"
            // Describes where THIS number came from: the bid only when the
            // peak day is itself a forecast day.
            : wTop.fromBid ? FORECAST_TITLE
            : 'Weekly peak crew/day — use a 2 or 4 week range to edit days'}>
            {project.isMaintenance ? (peak > 0 ? `Σ${peak}` : '0') : (wShown > 0 ? wShown : '0')}
          </td>
        );
      }

      const ds = col.dates[0];
      // Weekends are non-working by default (crews staff Mon–Fri), so empty
      // weekend cells read as shaded gaps. In-phase-window empty weekday
      // cells get a faint tint so the crew run is visible.
      const inSpan = crewRow.phaseSpan != null && ds >= crewRow.phaseSpan.start && ds <= crewRow.phaseSpan.end;
      const emptyBg = col.isWeekend ? 'var(--ew-n-100)' : inSpan ? 'var(--ew-n-50)' : undefined;

      // Maintenance parent = header row: read-only roll-up of its
      // sub-projects' crew for the day, never editable.
      // Selection overlay + drag handlers (#589). A maintenance parent can be
      // dragged across and shows as refused; it is never written.
      const sp = selCellProps(crewRow, ds);
      if (project.isMaintenance) {
        const rollup = maintenanceDayTotal(project.projectId, ds);
        return (
          <td key={ci} onMouseDown={sp.onMouseDown} onMouseEnter={sp.onMouseEnter} style={{
            ...TD, padding: '5px 3px', textAlign: 'center',
            borderLeft: weekDivider,
            // Deliberately NOT the crew density fill. Painting a roll-up like a
            // crew booking made the parent read as another crew line stacked on
            // top of its subs, so the grid looked as though the job were counted
            // twice — it isn't; only the subs feed the total (#296). A muted
            // subtotal treatment says "sum of the rows below" instead.
            background: col.isWeekend ? 'var(--ew-n-100)' : '#f8fafc',
            color: rollup > 0 ? 'var(--ew)' : 'var(--ew-n-500)',
            fontWeight: 600, fontStyle: 'italic', fontSize: 12, fontVariantNumeric: 'tabular-nums',
            ...sp.style,
          }} title={sp.title ?? (rollup > 0 ? `Subtotal — ${rollup} crew across this job's sub-projects (read-only). Counted once in the grand total, through the sub-project rows.` : undefined)}>
            {rollup > 0 ? `Σ${rollup}` : ''}
          </td>
        );
      }
      const meta = getPhaseCellMeta(crewRow, ds);
      const count = meta.count;
      // Only where nothing is scheduled on the PROJECT — a real entry always
      // wins, including one booked against the job's other crew line.
      const dayForecast = getCrewCount(project, ds) > 0 ? null : forecastCrewOn(forecast, ds);
      const isEditing = editingCell?.projectId === project.projectId
        && editingCell?.workDate === ds
        && (editingCell?.phase ?? null) === crewRow.phase;
      // Booked or anticipated — one number, drawn the same way (#425). Editing
      // still starts from `count`: a forecast is derived, so clicking a
      // forecast cell opens an EMPTY input rather than seeding the bid figure
      // as though someone had typed it.
      const shown   = crewLineValue(crewRow, ds);
      const fill    = crewFillStyle(shown);
      // Edit-mode markers — two orthogonal signals: COLOR = what kind of
      // change (orange = pinned day, blue = plain change), LINE = publish
      // state (dashed = staged/unpublished, solid = live).
      const marker: React.CSSProperties = !editMode ? {}
        : meta.isOverride && meta.isDraft ? { outline: '2px dashed var(--ew-performance)', outlineOffset: -2 }
        : meta.isOverride                 ? { outline: '2px solid var(--ew-performance)',  outlineOffset: -2 }
        : meta.isDraft                    ? { outline: '2px dashed var(--ew-quality)',     outlineOffset: -2 }
        : {};
      const markerTitle = !editMode ? undefined
        : meta.isOverride && meta.isDraft ? 'Pinned day — staged, publishes with your draft. Retype the row\'s base rate to un-pin.'
        : meta.isOverride                 ? 'Pinned day (live) — steppers leave it alone. Retype the row\'s base rate to un-pin.'
        : meta.isDraft                    ? 'Unpublished draft change'
        : undefined;
      // The survey chip marks the project's site visit — on a fab job it sits
      // on the Installation (field) line, not the shop line.
      const showSurveys = crewRow.phase !== 'fabrication';
      const cellSurveys = showSurveys ? (surveysByCell.get(`${project.projectId}|${ds}`) ?? []) : [];

      return (
        <td key={ci} style={{
          ...TD,
          padding: 0,
          position: 'relative',
          borderLeft: weekDivider,
          ...marker,
          background: isEditing ? '#f0fdf4' : ((fill.background as string | undefined) ?? emptyBg),
          ...sp.style,
        }}
        onMouseDown={sp.onMouseDown} onMouseEnter={sp.onMouseEnter}
        title={sp.title ?? markerTitle}>
          {cellSurveys.length > 0 && (
            <SurveyChip surveys={cellSurveys} onOpen={(id) => navigate(`/survey?request=${id}`)} />
          )}
          {isEditing ? (
            <input
              ref={inputRef}
              type="number" min={0} max={999}
              value={editValue}
              onChange={(e) => { editTouched.current = true; setEditValue(e.target.value); }}
              onBlur={() => commitEdit(crewRow, ds)}
              onKeyDown={(e) => handleCellKey(e, crewRow, ds)}
              // A BLOCK pasted into an open cell (the spreadsheet habit: click a
              // cell, Ctrl+V) pastes across the grid from here (#589). One
              // number still goes into the box as typed text — see isBlockPaste
              // for why blank trailing cells still make it a block.
              onPaste={(e) => {
                const text = e.clipboardData.getData('text/plain');
                if (!selectable || !isBlockPaste(text)) return;
                e.preventDefault();
                abandonedEdit.current = `${crewRow.key}|${ds}`;
                setEditingCell(null);
                const at = { rowKey: crewRow.key, ds };
                setSelection({ anchor: at, focus: at });
                pasteText(text, { anchor: at, focus: at });
              }}
              style={{ width: '100%', border: 'none', outline: '2px inset var(--ew-green)', padding: '5px 3px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--ew-dark)', background: 'transparent', fontFamily: 'var(--ew-font)', minHeight: 30, boxSizing: 'border-box' }}
            />
          ) : (
            <span
              onClick={() => {
                // A shift-click drew a selection; it must not also open the editor.
                if (swallowClick.current) { swallowClick.current = false; return; }
                if (rowEd) startEdit(crewRow, ds, count);
              }}
              title={sp.title ?? (!editMode
                ? (count === 0 && dayForecast != null ? FORECAST_TITLE : undefined)
                : rowEd ? 'Click to edit (stages as a draft) · drag or shift-click to select several' : 'Read-only — your role edits Fabrication jobs only')}
              style={{
                display: 'block', padding: '5px 3px', minHeight: 30,
                cursor: editMode && rowEd ? 'cell' : 'default', textAlign: 'center', fontSize: 12,
                fontWeight: shown > 0 ? 700 : 400,
                fontVariantNumeric: 'tabular-nums',
                color: shown > 0 ? (fill.color as string) : 'transparent',
              }}
            >
              {shown > 0 ? shown : '0'}
            </span>
          )}
        </td>
      );
    });
  }

  /** Remove a project from the forward schedule: clears its crew entries from
   *  today onward (history kept). Still-active projects keep an empty row via
   *  the active-project merge until they're completed/archived on Projects. */
  async function clearProjectFuture(project: {
    projectId: number; jobNumber: string; title: string;
    /** Present so the invalidation below can tell a sub-project apart — see there. */
    parentProjectId?: number | null;
  }) {
    if (!canEdit) return;
    try {
      const res = await manpowerApi.clearFutureEntries(project.projectId, localYmd(today));
      setRemoveConfirm(null);
      setNotifications((prev) => [
        `Removed ${project.jobNumber} · ${project.title} from the schedule going forward (${res.deleted} day-entr${res.deleted === 1 ? 'y' : 'ies'} cleared).`,
        ...prev,
      ].slice(0, 3));
      queryClient.invalidateQueries({ queryKey: ['manpower-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['manpower-sync-status'] });
      // Clearing a job's future entries removes published crew, so the no-crew
      // sections on both Today pages change. See invalidateReadinessQueries.
      void invalidateReadinessQueries(queryClient, project.projectId);
      // It can also remove the job's FIRST booked day, which the start-mismatch
      // flag is judged against (#385).
      //
      // ⚠️ Narrowing to this job is only safe while this action cannot reach a
      // maintenance SUB-project, whose crew rolls up into its PARENT's first
      // booked day — clearing a child while narrowing to the child would leave
      // the parent's card holding a mismatch that no longer exists. Two things
      // currently prevent it, and BOTH live somewhere else: the row menu branches
      // on `parentProjectId` and offers a sub "Delete sub" instead of this, and
      // `getSyncStatus` filters `parent_project_id IS NULL` so the other caller
      // never lists one either. That is an invariant held in two other files, so
      // it is re-stated here as code rather than trusted: a sub-project falls
      // back to clearing every summary.
      void invalidateStartAlignmentQueries(
        queryClient,
        project.parentProjectId != null ? undefined : project.projectId,
      );
    } catch {
      setNotifications((prev) => [`Failed to clear the schedule for ${project.jobNumber} — try again.`, ...prev].slice(0, 3));
    }
  }

  // ── Available MP editing ───────────────────────────────────────────────────
  function startAvailEdit() { setAvailableValue(String(availableMp)); setAvailableEdit(true); }
  function commitAvailEdit() {
    setAvailableEdit(false);
    const n = parseInt(availableValue, 10);
    if (!isNaN(n) && n > 0) updateConfigMutation.mutate(String(n));
  }

  useEffect(() => { if (editingCell) { inputRef.current?.focus(); inputRef.current?.select(); } }, [editingCell]);
  useEffect(() => { if (availableEdit) { availRef.current?.focus(); availRef.current?.select(); } }, [availableEdit]);
  // Weekly-peak columns are read-only aggregates — close any open cell editor
  // when leaving daily granularity.
  useEffect(() => {
    if (!daily) setEditingCell(null);
  }, [daily]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="mp-page" style={{ padding: '20px 28px 48px', fontFamily: 'var(--ew-font)', color: 'var(--ew-dark)' }}>
      {/* Phone-width tightening. The forecast grid already lives in an
          overflow-x: auto wrapper (below) — the only mobile risk is the page
          padding eating horizontal real estate, so trim it under 768px. */}
      <style>{`
        @media (max-width: 768px) {
          .mp-page { padding-left: 12px !important; padding-right: 12px !important; }
        }

        /* ── Print (#384) ──────────────────────────────────────────────────
           ⚠️ THIS COMMENT IS INSIDE A <style> TEMPLATE LITERAL, so it is not
           stripped by minification -- it ships in the JS itself, not merely in
           the sourcemap. Named a requester here until 2026-09-20. See the note
           on sourcemap in vite.config.ts; requesters belong in the issue.

           #384 asked for "printing/exporting", and on this page the two are one
           feature: the same button row offers both. What makes a
           printout usable here is almost entirely subtraction — app chrome,
           the view toggle, the filter row and every edit affordance are
           screen furniture that would otherwise eat the first page.

           ⚠️ The grid lives in an overflow-x: auto wrapper. On paper that
           wrapper CLIPS instead of scrolling, so a wide window silently
           prints a cropped schedule with no indication anything is missing.
           Forcing overflow visible is what makes the right-hand weeks
           actually reach the page. Landscape for the same reason. */
        /* The printed header is invisible on screen — the page already shows
           all of this in live controls. */
        .mp-print-summary { display: none; }
        /* Paper-only stand-in for something the print rules take away. */
        .mp-printonly { display: none; }

        /* Job # / project name -> that project's Overview. The grid is where a
           scheduler SEES a problem (a Start != Crew badge, a job needing crew)
           and the Overview is where a PM fixes it, so the row itself is the
           natural place to cross over.

           These inherit their colour and underline only on hover. They sit in
           the two densest columns of a grid that is otherwise numbers, and
           painting every row's first two cells link-blue would read as
           decoration rather than as structure; the pointer and the underline
           carry it instead.

           Nothing needed for print: they are already black and unadorned. */
        .mp-row-link { color: inherit; text-decoration: none; cursor: pointer; }
        .mp-row-link:hover,
        .mp-row-link:focus-visible { text-decoration: underline; }

        /* ── The measuring pass ──────────────────────────────────────────────
           printFit.ts switches this on for one synchronous layout read, to ask
           "how wide would this be on paper?". The page is pinned to the sheet
           width and the paper table sizing applied, so a table that fits simply
           reports the sheet width and takes no zoom, while one that cannot
           reports what it really needs. Invisible: it is added and removed
           inside a single frame, with no paint in between. */
        .mp-measuring .mp-page { width: 960px !important; }
        ${COMPACT_TABLE_CSS('.mp-measuring')}

        /* The printed header's own type. Sized in pt because this block only
           ever renders on paper, where pt is the honest unit. */
        .mp-print-title { font: 700 15pt/1.2 var(--ew-font); margin-bottom: 6px; }
        .mp-print-meta {
          display: grid;
          grid-template-columns: max-content 1fr;
          column-gap: 10px;
          row-gap: 1px;
          font-size: 9pt;
          line-height: 1.35;
          margin-bottom: 6px;
        }
        .mp-print-meta dt { font-weight: 700; color: #444; }
        .mp-print-meta dd { margin: 0; }

        @media print {
          .mp-print-summary {
            display: block !important;
            margin-bottom: 10px;
            font-size: 11pt;
            line-height: 1.45;
          }
          .mp-print-filtered { font-weight: 700; }
          .mp-printonly { display: inline !important; }

          .app-header, .app-tabs, .app-mobnav, .imp-banner { display: none !important; }
          .app-content { padding: 0 !important; }
          .mp-page { padding: 0 !important; }

          /* Every control on the page — view toggle, scope toggle, section
             chips, range nav, search, horizon, and the export buttons
             themselves. A control is an invitation to act, and paper cannot
             take one.

             ⚠️ Deliberately a rule about CONTROLS, not about specific
             toolbars: this page grows buttons regularly, and a whitelist of
             toolbar class names would silently stop covering them. The one
             figure that could have been lost this way — Available MP / day —
             is a <span> in both the editable and read-only branches, so it
             survives; .mp-print-keep is here for the next one that does not. */
          .mp-page button:not(.mp-print-keep),
          .mp-page select:not(.mp-print-keep),
          .mp-page input:not(.mp-print-keep) { display: none !important; }
          .mp-export { display: none !important; }

          /* ⚠️ And the rows those controls live in. Hiding a control does not
             hide its LABEL, and a print preview showed exactly that: with the
             filter and range rows emptied, the words "Sort", "PM", "SHOW:" and
             "RANGE" were left stranded on the page next to nothing, reading as
             a rendering fault. The window and the active filters are already
             stated in .mp-print-summary, so these rows have nothing left to
             contribute on paper. */
          .mp-noprint { display: none !important; }

          /* ⚠️ The CONTAINER only — deliberately not its descendants. Releasing
             overflow on every child also released the project-title cell, which
             for a read-only viewer is a fixed max-width with nowrap and
             overflow:hidden so a long job name ellipsises. Made visible, that
             name paints straight across the PM and crew columns. The clipping
             this rule exists to prevent is the wrapper's, so the wrapper is all
             it touches. */
          .mp-scroll { overflow: visible !important; max-height: none !important; }

          /* ── Fit the sheet ────────────────────────────────────────────────
             zoom, measured in printFit.ts and set as an inline custom property
             just before the dialog opens. The grid's width is DATA — 18 columns
             at two weeks, 32 at four — so no fixed type size fits every range.
             zoom rather than transform because zoom reflows, and pagination has
             to see the shrunken size or it breaks pages in the wrong places. */
          .mp-page { zoom: var(--mp-print-zoom, 1); }

          /* Tighter than the screen, because paper has no hover and no scroll:
             every column has to be on it at once. */
          ${COMPACT_TABLE_CSS('')}

          /* ⚠️ STICKY IS WHY ROWS REPEATED. The grid pins its two header tiers,
             the GRAND TOTAL band and the HEADROOM footer with position:sticky —
             51 cells of it. A stuck element is painted at its stuck offset on
             EVERY printed page, so the totals bands reappeared partway down
             sheet after sheet, overlapping real rows. On paper nothing scrolls,
             so nothing needs pinning: thead's table-header-group below is the
             correct, paginated way to repeat a header. */
          .mp-page th, .mp-page td { position: static !important; }

          table { page-break-inside: auto; }
          /* Never split a row across a page. */
          tr, thead tr, tfoot tr { page-break-inside: avoid; break-inside: avoid; }
          /* Column headers repeat on every sheet… */
          thead { display: table-header-group; }
          /* …but the HEADROOM footer prints ONCE, at the end. As a real tfoot
             the browser repeats it on every page, which is the duplicate row a
             reader would try to reconcile against the one above it. */
          tfoot { display: table-row-group; }

          /* A chart is a single figure — splitting one across a page break
             leaves half an axis on one sheet and half a legend on the next. */
          .mp-chart-card { page-break-inside: avoid; break-inside: avoid; }
          /* Charts shrink to the sheet instead of scrolling inside their box:
             the screen floor that keeps the type readable (gapCharts' VIEW_W)
             is what pushed the dark table bands off the right-hand edge. */
          .mp-chart-card svg { min-width: 0 !important; width: 100% !important; }

          /* Backgrounds carry meaning on this grid (crew fill, weekends, the
             dark header band), so ask the browser to keep them. */
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        /* Landscape: the crew grid is far wider than it is tall, and portrait
           costs roughly a third of the columns. */
        @page { size: landscape; margin: 10mm; }
      `}</style>

      {/* ── Printed header ──
          Screen-invisible; the print stylesheet reveals it.

          ⚠️ This exists because the print rule hides every CONTROL, and on this
          page the controls ARE the context: the scope toggle, the division
          chips and the PM filter all decide which jobs and which totals appear,
          and all three are buttons or a select. Hiding them left a sheet of
          paper that looks like the company-wide crew schedule whatever it
          actually contains — and the default scope is Self-Perform, so even an
          untouched printout silently omits Fabrication. Same reasoning as the
          workbook's FILTERED note, in the format that cannot carry a Summary
          sheet. */}
      {viewMode === 'schedule' && (
        <div className="mp-print-summary" aria-hidden>
          <div className="mp-print-title">Project Tracker — crew schedule</div>

          {/* ⚠️ A LABELLED GRID, not a sentence. Everything here decides which
              jobs and which totals are on the sheet, and a reader checking
              "is this the fortnight I asked for, and is Fabrication in it?"
              should find each answer in a fixed place rather than parsing prose.
              Only the filters that are actually ON get a row — an empty one
              would read as a setting rather than as "no filter". */}
          <dl className="mp-print-meta">
            <dt>Dates</dt>
            <dd>
              {formatRangeLabel(rangeStart, addDays(rangeStart, weeks * 7 - 1))}
              {' · '}{weeks} week{weeks === 1 ? '' : 's'}
              {' · '}{daily ? 'daily crew' : 'weekly peak crew/day'}
            </dd>

            <dt>Divisions</dt>
            <dd>
              {SCOPE_LABELS[scope]}
              {hiddenSectionLabels.length > 0 && <> — hidden: {hiddenSectionLabels.join(', ')}</>}
            </dd>

            {mismatchOnly && (<><dt>Start vs crew</dt><dd>Only jobs whose planned start disagrees with their first booked crew day</dd></>)}
            {travelFilter !== 'all' && (
              <><dt>Job type</dt><dd>Only {travelFilter === 'travel' ? 'Travel' : 'Local'} jobs</dd></>
            )}
            {pmFilter && (<><dt>PM</dt><dd>{pmFilter}</dd></>)}
            {search.trim() && (<><dt>Job search</dt><dd>“{search.trim()}”</dd></>)}
            {systacHidden && (<><dt>SYSTAC pursuits</dt><dd>Hidden</dd></>)}

            <dt>Jobs shown</dt>
            <dd>
              {rows.length} job{rows.length === 1 ? '' : 's'}
              {isFiltered && <> — the totals below cover these only, not the whole company</>}
            </dd>

            <dt>Printed</dt>
            <dd>{fmtDate(todayInputValue())}</dd>
          </dl>

          {/* Paper outlives the session it was printed in, and a draft printout
              is indistinguishable from a live one once it leaves the printer. */}
          {editMode && (
            <div className="mp-print-filtered">
              UNPUBLISHED DRAFT — contains staged crew changes, from every scheduler on the board,
              that PMs cannot see in the Project Tracker. Do not distribute.
            </div>
          )}
        </div>
      )}

      {/* ── Top bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
        {/* mp-noprint: the printed header carries a fuller title of its own
            ("Project Tracker — crew schedule"), so leaving this in gave the
            sheet two headings in a row saying nearly the same thing. */}
        <h2 className="mp-noprint" style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
          {VIEW_LABELS[viewMode].heading}
        </h2>

        {/* View toggle */}
        <div className="mp-noprint" style={{ display: 'inline-flex', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', overflow: 'hidden', marginRight: viewMode === 'schedule' ? 0 : 'auto' }}>
          {VIEW_ORDER.map((mode) => {
            const active = viewMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                style={{
                  padding: '5px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: 'var(--ew-font)',
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? 'var(--ew-green)' : 'var(--ew-white)',
                  color: active ? 'var(--ew-white)' : 'var(--ew-n-500)',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                {VIEW_LABELS[mode].toggle}
                {mode === 'requests' && openBadge && (
                  <span
                    aria-label={`${openCountQ.data?.open} open`}
                    style={{
                      marginLeft: 7, padding: '1px 6px', borderRadius: 'var(--ew-r-pill)', fontSize: 10,
                      fontVariantNumeric: 'tabular-nums', letterSpacing: 0,
                      background: active ? 'var(--ew-white)' : 'var(--ew-green)',
                      color: active ? 'var(--ew-green)' : 'var(--ew-white)',
                    }}
                  >
                    {openBadge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Scope: company-wide vs the Fabrication lens (capacity ladder). */}
        {viewMode === 'schedule' && (
          <div className="mp-noprint" style={{ display: 'inline-flex', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', overflow: 'hidden', marginRight: 'auto', marginLeft: -6 }}>
            {([
              ['sp',  'Self-Perform', 'Every division except Fabrication — the crew available for self-perform work'],
              ['all', 'All Divisions', 'Every division, Fabrication included'],
              ['fab', 'Fabrication', 'Fabrication jobs only, with shop-vs-field capacity totals'],
            ] as const).map(([s, label, hint]) => {
              const active = scope === s;
              return (
                <button
                  key={s}
                  type="button"
                  title={hint}
                  onClick={() => setScope(s)}
                  style={{
                    padding: '5px 14px', fontSize: 12, fontWeight: 700, fontFamily: 'var(--ew-font)',
                    border: 'none', cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase',
                    background: active ? 'var(--ew-dark)' : 'var(--ew-white)',
                    color: active ? 'var(--ew-white)' : 'var(--ew-n-500)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {viewMode === 'schedule' && canEdit && daily && !editMode && (
          <button
            className="btn btn-primary"
            style={{ fontSize: 12, padding: '6px 14px' }}
            title="Stage crew changes as a draft PMs can't see, then publish"
            onClick={() => { setEditingCell(null); setEditMode(true); }}
          >
            ✎ Edit Schedule{drafts.draftCount > 0 ? ` (${drafts.draftCount} pending)` : ''}
          </button>
        )}

        {/* #384 asked to print or export the crew schedule.
            Ungated: this writes nothing and shows only what the grid already
            shows to whoever is looking at it. */}
        {viewMode === 'schedule' && (
          <ExportMenu
            what="the crew schedule"
            // ⚠️ Disabled while a new window is in flight, not just when empty.
            // useScheduleData holds the PREVIOUS window's rows on screen through
            // a range change (placeholderData), while columns, dateStrs and the
            // range label switch to the new one immediately. Exporting in that
            // gap produces a file headed with the new dates and filled from the
            // old fetch — mostly zeros, and nothing about it looks wrong.
            // ⚠️ And while Available MP is saving. Clicking Excel straight after
            // typing a new figure BLURS the input first, which commits the
            // config mutation — so one click both changes the number and writes
            // the file, and without this the workbook's capacity and headroom
            // would be the value the user just replaced.
            // ⚠️ And on dataReady, which covers the two INDEPENDENT queries this
            // export also reads: project-options (the jobs with no crew in the
            // window, and every PM name) and config (capacity). Either can still
            // be loading, or have failed, while the schedule has landed — and
            // both fall back silently, to no rows and to a hard-coded 144.
            disabled={rows.length === 0 || isFetching || updateConfigMutation.isPending || !dataReady}
            onExcel={() => exportScheduleXlsx(scheduleExport)}
          />
        )}

        {viewMode === 'schedule' && (
          <button
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '6px 12px' }}
            onClick={() => setShowHelp(true)}
            title="How the board works: reading it, editing it, and what the cell markers mean"
            aria-label="Open crew schedule help"
          >
            ? Help
          </button>
        )}
        {/* Sync Projects — ADMIN ONLY (product owner, 2026-07-29). It clears future crew
            from jobs wholesale. Note this stays a UI-only restriction: it drives
            DELETE /manpower/schedule/project/:id/future, which is shared with the
            per-row "✕ clear future crew" control that manpower.edit holders are
            meant to keep. Raising that route would break the row control. */}
        {viewMode === 'schedule' && isAdmin && (
          <button
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '6px 12px', gap: 5 }}
            onClick={() => setShowSync(true)}
            title="Reconcile the schedule with the Projects tab — clear future crew from jobs that shouldn't be here anymore"
          >
            ⇆ Sync Projects
          </button>
        )}
        {viewMode === 'schedule' && (
          <>
            {/* Bulk Excel import — ADMIN ONLY (product owner, 2026-07-29). It rewrites the
                schedule wholesale, so it sits above manpower.edit. The API route
                is raised to admin.access to match, rather than leaving this as a
                UI-only restriction. */}
            {isAdmin && (
            <button
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '6px 12px', gap: 5 }}
              onClick={() => setShowImport(true)}
            >
              ↑ Import Excel
            </button>
            )}

            {/* Available MP badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--ew-green-50)', border: '1px solid var(--ew-green-100)', borderRadius: 'var(--ew-r-md)', padding: '5px 16px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ew-green-700)' }}>
            Available MP / day
          </span>
          {availableEdit && canEdit && !fabEditOnly ? (
            <input
              ref={availRef}
              type="number" min={0}
              value={availableValue}
              onChange={(e) => setAvailableValue(e.target.value)}
              onBlur={commitAvailEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitAvailEdit();
                else if (e.key === 'Escape') setAvailableEdit(false);
              }}
              style={{ width: 60, fontSize: 22, fontWeight: 700, textAlign: 'center', border: '2px solid var(--ew-green)', borderRadius: 'var(--ew-r-md)', padding: '1px 4px', color: 'var(--ew-green-700)', background: 'var(--ew-white)', fontFamily: 'var(--ew-font)' }}
            />
          ) : canEdit && !fabEditOnly ? (
            <span
              onClick={startAvailEdit}
              title="Click to edit available manpower per day"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 22, fontWeight: 700, color: 'var(--ew-green-700)', cursor: 'pointer', minWidth: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums', borderBottom: '1px dashed var(--ew-green)' }}
            >
              {availableMp}
              <span aria-hidden style={{ fontSize: 12, opacity: 0.7 }}>✎</span>
            </span>
          ) : (
            <span
              title="Available manpower per day (editable by Admin / Manpower Editor)"
              style={{ fontSize: 22, fontWeight: 700, color: 'var(--ew-green-700)', minWidth: 32, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
            >
              {availableMp}
            </span>
          )}
        </div>

            {/* Temps active (#584): typed by hand, counted INSIDE Available MP. */}
            <TempsActiveBox availableMp={availableMpKnown ? availableMp : null} canEdit={canEdit && !fabEditOnly} />

            {/* Subcontractors (#688): typed by hand, counted INSIDE Available MP with the temps. */}
            <SubcontractorsActiveBox availableMp={availableMpKnown ? availableMp : null} canEdit={canEdit && !fabEditOnly} />
          </>
        )}
      </div>

      {/* Field-leader CRUD is manpower.edit ONLY (POST/PATCH/DELETE
          /manpower/leaders) — manpower.edit.fab is NOT accepted there, so a
          fab-scoped editor must not be offered Add / Edit / Remove. */}
      {viewMode === 'leaders' ? (
        <LeadersView canEdit={canEdit && !fabEditOnly} />
      ) : viewMode === 'requests' ? (
        // Read-only and ungated beyond auth, matching the crew grid and the
        // History view: a PM who cannot edit the schedule still has to be able
        // to see the request they raised. Deciding one needs manpower.edit and
        // arrives in slice 3.
        <RequestsView canTriage={canEdit && !fabEditOnly} />
      ) : viewMode === 'gap' ? (
        // Read-only, ungated beyond auth — matching Leaders and Travel. #396's
        // audience explicitly includes HR, and there is nothing here to edit:
        // the numbers come from the crew schedule and the travel roster, whose
        // own edit surfaces are the Crew Schedule and Travel views.
        <GapAnalysisView />
      ) : viewMode === 'history' ? (
        // Read-only and ungated beyond auth, matching the crew grid: a PM who
        // cannot edit the schedule still needs to see that their job moved and
        // who moved it. That is the whole point of #350.
        <HistoryView />
      ) : (
      <>
      {/* Screen-only: it explains which CONTROLS you do not have, and the
          printout has no controls for anyone. */}
      {!canEdit && (
        <div className="mp-noprint" style={{ marginBottom: 10, fontSize: 12, color: 'var(--ew-n-500)' }}>
          You are in read-only mode. Edit controls are limited to Admin and ManpowerEditor roles.
        </div>
      )}
      {fabEditOnly && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--ew-n-500)' }}>
          Your role edits <b>Fabrication-division jobs only</b> — other rows are read-only, and Publish folds in just your fabrication drafts.
        </div>
      )}

      {notifications.length > 0 && (
        <div style={{ marginBottom: 12, display: 'grid', gap: 6 }}>
          {notifications.map((note, idx) => (<div key={idx} style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 'var(--ew-r-md)', padding: '8px 10px', fontSize: 12 }}>⚠ {note}</div>))}
        </div>
      )}

      {/* ── Section filter chips + job search ── */}
      {sections.length > 0 && (
        <div className="mp-noprint" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {/* Job search: isolate matching rows; subtotals + Grand Total follow. */}
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginRight: 8 }}>
            <span aria-hidden style={{ position: 'absolute', left: 9, fontSize: 12, color: 'var(--ew-n-400)', pointerEvents: 'none' }}>⌕</span>
            {/* type=text, not search — the browser's native clear ✕ would double
                up with our custom one. */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }}
              placeholder="Search job # or name…"
              aria-label="Search jobs on the schedule"
              style={{
                width: 210, padding: '5px 26px 5px 26px', fontSize: 12,
                fontFamily: 'var(--ew-font)', color: 'var(--ew-dark)',
                border: `1px solid ${searchNorm ? 'var(--ew-green)' : 'var(--ew-n-200)'}`,
                borderRadius: 'var(--ew-r-pill)', background: 'var(--ew-white)', outline: 'none',
              }}
            />
            {searchNorm && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear job search"
                style={{ position: 'absolute', right: 6, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--ew-n-500)', padding: 2 }}
              >
                ✕
              </button>
            )}
          </div>
          {searchNorm && (
            <span style={{ fontSize: 11, fontWeight: 600, color: rows.length > 0 ? 'var(--ew-green-700)' : 'var(--ew-danger)', marginRight: 4 }}>
              {rows.length === 0 ? 'No jobs match' : `${rows.length} job${rows.length === 1 ? '' : 's'} — totals reflect this view`}
            </span>
          )}
          {/* Row order + PM filter (PM request 2026-07-25). Sorting reorders
              rows INSIDE each division section — sections themselves keep their
              fixed order so the subtotals and capacity headroom stay meaningful. */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--ew-n-500)' }}>
            Sort
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as RowSort)}
              aria-label="Sort jobs within each section"
              style={{ fontSize: 12, fontFamily: 'var(--ew-font)', padding: '4px 6px', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-sm)', background: 'var(--ew-white)', color: 'var(--ew-dark)', cursor: 'pointer' }}
            >
              <option value="jobNumber">Job #</option>
              <option value="title">Job name</option>
              <option value="pm">PM</option>
            </select>
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: pmFilter ? 'var(--ew-green-700)' : 'var(--ew-n-500)' }}>
            PM
            <select
              value={pmFilter}
              onChange={(e) => setPmFilter(e.target.value)}
              aria-label="Show only jobs for this project manager"
              style={{ fontSize: 12, fontFamily: 'var(--ew-font)', padding: '4px 6px', border: `1px solid ${pmFilter ? 'var(--ew-green)' : 'var(--ew-n-200)'}`, borderRadius: 'var(--ew-r-sm)', background: 'var(--ew-white)', color: 'var(--ew-dark)', cursor: 'pointer', maxWidth: 150 }}
            >
              <option value="">All PMs</option>
              {pmOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>

          {/* Travel / Local. A segmented control rather than another select,
              because it is three fixed states and the Type column it filters is
              already a two-value badge — a dropdown would hide the options that
              are the whole point. Colored when narrowed, like the PM filter
              beside it, so an active filter is visible at a glance and a board
              showing half the jobs never looks like the whole one. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: travelFilter !== 'all' ? 'var(--ew-green-700)' : 'var(--ew-n-500)' }}>
            TYPE
            <span
              role="group"
              aria-label="Show only travel or only local jobs"
              style={{ display: 'inline-flex', border: `1px solid ${travelFilter !== 'all' ? 'var(--ew-green)' : 'var(--ew-n-200)'}`, borderRadius: 'var(--ew-r-sm)', overflow: 'hidden' }}
            >
              {([
                ['all', 'All', 'Every job, travel and local'],
                ['travel', 'Travel', 'Only jobs classified Travel'],
                ['local', 'Local', 'Only jobs classified Local'],
              ] as const).map(([value, label, hint]) => {
                const active = travelFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    title={hint}
                    aria-pressed={active}
                    onClick={() => setTravelFilter(value)}
                    style={{
                      padding: '3px 9px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer',
                      fontFamily: 'var(--ew-font)', letterSpacing: '0.03em',
                      background: active ? 'var(--ew-green)' : 'var(--ew-white)',
                      color: active ? 'var(--ew-white)' : 'var(--ew-n-500)',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </span>
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ew-n-500)', marginRight: 4 }}>
            Show:
          </span>
          {sections.map((section) => {
            const visible = !hiddenSections.has(section.key);
            const palette = section.isNeedsScheduling
              ? { fg: '#92400e', bg: '#fef3c7', border: '#fcd34d' }
              : section.divisionId === null
                ? { fg: 'var(--ew-n-500)', bg: 'var(--ew-n-50)', border: 'var(--ew-n-300)' }
                : { fg: 'var(--ew-green-700)', bg: 'var(--ew-green-50)', border: 'var(--ew-green-100)' };
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => toggleSection(section.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', fontSize: 12, fontWeight: 600,
                  border: `1px solid ${visible ? palette.border : 'var(--ew-n-200)'}`,
                  borderRadius: 'var(--ew-r-pill)', cursor: 'pointer', userSelect: 'none',
                  background: visible ? palette.bg : 'var(--ew-white)',
                  color:      visible ? palette.fg : 'var(--ew-n-400)',
                  fontFamily: 'var(--ew-font)',
                  opacity:    visible ? 1 : 0.6,
                  textDecoration: visible ? 'none' : 'line-through',
                }}
                title={visible ? `Hide ${section.label} section` : `Show ${section.label} section`}
              >
                {visible ? '✓' : '○'} {section.label}
                <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>
                  ({section.projects.length})
                </span>
              </button>
            );
          })}
          {/* ⚠️ Start ≠ Crew ISOLATES; every other chip on this row hides. The
              label carries that difference — "✕" to clear rather than a ✓/○ pair
              — because a control that looks like its neighbours and behaves the
              opposite way is worse than one that plainly reads differently.
              Absent when nothing disagrees, so a clean portfolio has no dead
              control sitting on the toolbar.

              ⚠️ …but NEVER absent while it is ON, which is not the same condition.
              The count moves with the other filters, so hiding the division that
              held the last mismatched job dropped it to zero, unmounted the chip,
              and left the filter still applied — an empty grid whose only control
              had just vanished. describeNarrowings names it in the empty state,
              which says what is wrong without offering a way out. A filter that
              can hide its own switch is a trap. */}
          {(mismatchCount > 0 || mismatchOnly) && (
            <button
              type="button"
              onClick={() => setMismatchOnly((v) => !v)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${mismatchOnly ? '#fda4af' : 'var(--ew-n-200)'}`,
                borderRadius: 'var(--ew-r-pill)', cursor: 'pointer', userSelect: 'none',
                background: mismatchOnly ? '#ffe4e6' : 'var(--ew-white)',
                color:      mismatchOnly ? '#9f1239' : 'var(--ew-n-500)',
                fontFamily: 'var(--ew-font)',
              }}
              aria-pressed={mismatchOnly}
              /* ⚠️ "Clear this filter", not "Show every job again". Pressing it
                 restores only what the OTHER narrowings allow — scope, Type, the
                 search and any hidden divisions all still apply — and a control
                 that promises the whole company while handing back a subset is
                 the same small untruth describeNarrowings and the workbook's
                 FILTERED note exist to prevent. */
              title={mismatchOnly
                ? 'Clear this filter — the other filters stay on'
                : 'Show only jobs whose planned start disagrees with their first booked crew day'}
            >
              {mismatchOnly ? '✕ ' : ''}⚠ Start ≠ Crew
              <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>
                ({mismatchCount})
              </span>
            </button>
          )}
          {/* SYSTAC is a cross-division filter: SYSTAC jobs sit inside their
              division sections, and this chip hides/shows them everywhere. */}
          {systacCount > 0 && (
            <button
              type="button"
              onClick={() => toggleSection('systac')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${!systacHidden ? '#fed7aa' : 'var(--ew-n-200)'}`,
                borderRadius: 'var(--ew-r-pill)', cursor: 'pointer', userSelect: 'none',
                background: !systacHidden ? '#fff7ed' : 'var(--ew-white)',
                color:      !systacHidden ? 'var(--ew-performance)' : 'var(--ew-n-400)',
                fontFamily: 'var(--ew-font)',
                opacity:    !systacHidden ? 1 : 0.6,
                textDecoration: !systacHidden ? 'none' : 'line-through',
              }}
              title={!systacHidden ? 'Hide SYSTAC jobs in every division' : 'Show SYSTAC jobs in every division'}
            >
              {!systacHidden ? '✓' : '○'} SYSTAC
              <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>
                ({systacCount})
              </span>
            </button>
          )}
        </div>
      )}

      {/* ── Edit-mode tray: unpublished count + peak day + publish/reset ── */}
      {editMode && (
        <AssignmentTray
          peakDay={Math.max(0, ...colTotals)}
          peakFab={scope === 'fab' ? Math.max(0, ...fabTotals) : undefined}
          peakInstall={scope === 'fab' ? Math.max(0, ...installTotals) : undefined}
          fabScope={scope === 'fab'}
          draftCount={drafts.draftCount}
          lastDraftAt={drafts.lastDraftAt}
          othersDraftCount={drafts.othersDraftCount}
          otherDraftEditors={drafts.otherDraftEditors}
          busy={drafts.busy}
          onPublish={() => drafts.publish.mutate(undefined, {
            onSuccess: (r) => {
              // Publish means "done" — drop straight back to the read view.
              setEditMode(false);
              setNotifications((prev) => [`Published ${r.published} change${r.published === 1 ? '' : 's'} to the live schedule.`, ...prev].slice(0, 3));
            },
          })}
          onReset={() => drafts.reset.mutate(undefined, {
            onSuccess: (r) => setNotifications((prev) => [`Discarded ${r.discarded} unpublished change${r.discarded === 1 ? '' : 's'}.`, ...prev].slice(0, 3)),
          })}
          onDone={() => setEditMode(false)}
        >
          {/* Multi-cell selection (#589): what is selected and what an
              action would refuse. The report of what the last one DID sits
              below the tray. */}
          {selectable && (
            <SelectionBar
              info={selInfo}
              skipWeekends={skipWeekends} onSkipWeekends={setSkipWeekends}
              value={selValue} onValue={setSelValue} inputRef={selInputRef}
              busy={drafts.busy || isFetching}
              onSet={selSet} onClear={selClear} onCopy={selCopy}
              onDeselect={() => setSelection(null)}
            />
          )}
        </AssignmentTray>
      )}

      {editMode && bulkReport && (
        <BulkReportPanel report={bulkReport} onDismiss={() => setBulkReport(null)} />
      )}

      {/* ── Window navigation + range presets ── */}
      <div className="mp-noprint" style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 14, flexWrap: 'wrap' }}>
        <button style={NAV_BTN} onClick={goBack}    aria-label="Back 1 week">‹</button>
        <button style={TODAY_BTN} onClick={goToday}>Today</button>
        <button style={NAV_BTN} onClick={goForward} aria-label="Forward 1 week">›</button>
        <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 600, color: 'var(--ew-dark)' }}>
          {formatRangeLabel(rangeStart, addDays(rangeStart, weeks * 7 - 1))}
        </span>
        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ew-n-400)' }}>
          {daily ? '7-day calendar · daily crew' : 'Weekly peak crew/day'}
        </span>
        {/* Quiet in-place indicator. The grid keeps the previous window's rows
            while this is up, so nothing moves and you keep your scroll spot. */}
        {isFetching && !isLoading && (
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ew-quality)', fontWeight: 600 }}>updating…</span>
        )}

        {/* Go to a date or range (#588) — see GridJumpControl / lib/gridJump. */}
        <GridJumpControl
          from={jumpFrom} to={jumpTo} capped={jumpCapped}
          onChange={applyJump} onClear={clearJump}
        />

        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ew-n-500)' }}>Range</span>
          <span style={{ display: 'inline-flex', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', overflow: 'hidden' }}>
            {RANGE_PRESETS.map((w) => {
              const active = weeks === w;
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => choosePreset(w)}
                  style={{
                    padding: '5px 11px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--ew-font)',
                    background: active ? 'var(--ew-green)' : 'var(--ew-white)',
                    color: active ? 'var(--ew-white)' : 'var(--ew-n-500)',
                  }}
                >
                  {w} wk
                </button>
              );
            })}
          </span>
        </span>
      </div>

      {/* ── Schedule grid ──
          The wrapper owns BOTH scroll axes so the two-tier header can pin on
          vertical scroll (position: sticky is relative to the scroll container). */}
      <div className="mp-scroll" style={{ overflow: 'auto', maxHeight: '72vh', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--ew-n-200)', boxShadow: 'var(--ew-shadow-2)' }}>
        {/* `isLoading` is now only true on the FIRST load — once there are rows,
            react-query holds them while a new date window fetches, so the table
            stays mounted and the scroll position survives a range change.
            Swapping it out was what threw schedulers back to the top of the
            list. `isFetching` drives the quiet indicator above the grid. */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 64, color: 'var(--ew-n-400)', fontSize: 14 }}>Loading schedule…</div>
        ) : isError ? (
          <div style={{ textAlign: 'center', padding: 64, color: 'var(--ew-danger)', fontSize: 14 }}>Failed to load schedule.</div>
        ) : (
          <table className="mp-fit" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, fontFamily: 'var(--ew-font)' }}>
            <thead>
              {/* Tier 1 — week-group band (daily granularity only). Current week
                  highlighted green so "this week" reads instantly. */}
              {daily && (
                <tr>
                  <th colSpan={LABEL_COLS} style={{ ...TH, position: 'sticky', top: 0, zIndex: 6, height: 22, padding: '4px 10px' }} aria-hidden />
                  {groups.map((g) => (
                    <th key={g.weekStart} colSpan={g.span} style={{
                      ...TH,
                      position: 'sticky', top: 0, zIndex: 5, height: 22, padding: '4px 6px',
                      fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
                      background: g.isCurrentWeek ? 'var(--ew-green)' : 'var(--ew-dark)',
                      borderLeft: '2px solid rgba(255,255,255,0.2)',
                    }}>
                      {g.label}
                    </th>
                  ))}
                </tr>
              )}
              {/* Tier 2 — day (or week) cells. Weekends read as non-work (darker).
                  Fixed height so the Grand Total tier below gets a stable offset. */}
              <tr>
                <th style={{ ...TH, textAlign: 'left', width: 72,  padding: '7px 10px', height: 38, boxSizing: 'border-box', position: 'sticky', top: daily ? 30 : 0, zIndex: 6 }}>Job #</th>
                <th style={{ ...TH, textAlign: 'left', width: 170, minWidth: 140, padding: '7px 10px', height: 38, boxSizing: 'border-box', position: 'sticky', top: daily ? 30 : 0, zIndex: 6 }}>Project</th>
                <th style={{ ...TH, textAlign: 'left', width: 110, minWidth: 90, padding: '7px 10px', height: 38, boxSizing: 'border-box', position: 'sticky', top: daily ? 30 : 0, zIndex: 6 }}>PM</th>
                {/* Travel / Local. The manpower lead's classification, and the one the Gap
                    Analysis tab splits every figure on — see TypeToggle. */}
                <th style={{ ...TH, textAlign: 'left', width: 74, minWidth: 66, padding: '7px 8px', height: 38, boxSizing: 'border-box', position: 'sticky', top: daily ? 30 : 0, zIndex: 6 }}>Type</th>
                {columns.map((col, i) => (
                  <th key={i} style={{
                    ...TH,
                    width: 58,
                    height: 38, boxSizing: 'border-box',
                    position: 'sticky', top: daily ? 30 : 0, zIndex: 5,
                    background: col.isToday ? 'var(--ew-green-700)'
                              : col.isWeekend ? '#2c2c2c'
                              : (!daily && col.isCurrentWeek) ? 'var(--ew-green)'
                              : 'var(--ew-dark)',
                    borderLeft: col.isWeekStart && i > 0 ? '2px solid rgba(255,255,255,0.2)' : undefined,
                  }}>
                    <div style={{ fontSize: 9, opacity: 0.65, fontWeight: 500, letterSpacing: '0.04em' }}>{col.labelTop}</div>
                    <div style={{ fontSize: 11, fontWeight: 700 }}>{col.labelBottom}</div>
                  </th>
                ))}
              </tr>
              {/* Tier 3 — company Grand Total pinned under the dates (PM
                  request: the overall number visible at the top while
                  scrolling). Headroom keeps the bottom pin. */}
              <tr>
                <th style={{ ...TH, textAlign: 'right', padding: '6px 10px', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--ew-green)', position: 'sticky', top: daily ? 68 : 38, zIndex: 6, whiteSpace: 'nowrap' }} colSpan={LABEL_COLS}>
                  Grand Total
                </th>
                {columns.map((col, i) => {
                  const total = colValue(colTotals, col);
                  return (
                    <th key={i} style={{
                      ...TH, padding: '6px 3px', fontSize: 12, fontVariantNumeric: 'tabular-nums',
                      background: 'var(--ew-green)',
                      position: 'sticky', top: daily ? 68 : 38, zIndex: 5,
                      borderLeft: col.isWeekStart && i > 0 ? '2px solid rgba(255,255,255,0.2)' : undefined,
                      color: total > availableMp ? 'var(--ew-safety)' : total === 0 ? 'rgba(255,255,255,0.25)' : 'var(--ew-white)',
                    }}>
                      {total > 0 ? total : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {activeSections.length === 0 ? (
                <tr>
                  <td colSpan={LABEL_COLS + columns.length} style={{ textAlign: 'center', padding: '48px 32px', color: 'var(--ew-n-400)', fontSize: 14 }}>
                    {/* ⚠️ The narrowings decide the wording, NOT whether
                        `sections` is empty. A period is only reported as empty
                        when nothing is filtering it — otherwise the grid names
                        what is hiding the rows. See activeNarrowings. */}
                    {activeNarrowings.length === 0
                      ? 'No schedule entries found for this period.'
                      : <>No jobs match {activeNarrowings.join(', ')}. Clear or change {activeNarrowings.length > 1 ? 'one of them' : 'it'} to see the rest.</>}
                  </td>
                </tr>
              ) : (
                activeSections.map((section) => {
                  const headerBg = section.isNeedsScheduling ? '#fef3c7'
                                 : section.divisionId === null ? 'var(--ew-n-100)'
                                 : 'var(--ew-green-50)';
                  const headerFg = section.isNeedsScheduling ? '#92400e'
                                 : section.divisionId === null ? 'var(--ew-n-500)'
                                 : 'var(--ew-green-700)';
                  const subtotal = sectionTotals.get(section.key) ?? [];

                  return (
                    <Fragment key={section.key}>
                      {/* Section header */}
                      <tr style={{ background: headerBg, borderTop: '2px solid var(--ew-n-200)' }}>
                        <td colSpan={LABEL_COLS + columns.length} style={{
                          padding: '6px 12px', fontSize: 11, fontWeight: 700,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          color: headerFg,
                        }}>
                          ▶ {section.label} <span style={{ opacity: 0.7, fontWeight: 600 }}>({section.projects.length})</span>
                          {section.isNeedsScheduling && (
                            <span style={{ marginLeft: 10, fontSize: 10, fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', color: '#92400e', opacity: 0.85 }}>
                              starts in the visible window with no crew booked
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Project rows. A fabrication-division job renders a
                          header row (title/badges/progress/actions) plus TWO
                          crew lines (shop fabrication + field installation);
                          every other project keeps its classic single row. */}
                      {section.projects.map((project) => {
                        const crewRows = buildCrewRows(project);
                        const fabSplit = crewRows.length > 1;
                        // Blended progress % (same weighting as the project
                        // Overview) — shown when any phase carries a %.
                        const pct = project.phases && project.phases.some((ph) => ph.completionPct != null)
                          ? weightedOverall(project.phases).overallPct
                          : null;
                        // Is the crew on this row the bid's rather than booked?
                        // Decides the Needs Scheduling badge wording below.
                        const bidCrewShown = showsForecastCrew(project, dateStrs);
                        // #385: the PM's planned start against the Scheduler's
                        // first booked crew day. Both are server-derived and
                        // window-independent, so the flag belongs to the JOB and
                        // does not come and go as the grid pages through weeks.
                        const mismatch = startMismatch(project.currentStartDate, project.firstCrewDate);
                        return (
                        <Fragment key={project.projectId}>
                        <tr style={{
                          background: project.isMaintenance ? '#eff6ff'
                                    : section.isNeedsScheduling ? '#fffbeb'
                                    : mismatch ? START_MISMATCH_ROW_BG
                                    : project.isSystac ? '#fdfaf5'
                                    : 'var(--ew-white)',
                          borderLeft: project.isMaintenance ? '3px solid #3b82f6'
                                    : section.isNeedsScheduling ? '3px solid #d97706'
                                    : mismatch ? `3px solid ${START_MISMATCH_ROW_ACCENT}`
                                    : project.isSystac ? '3px solid var(--ew-performance)'
                                    : '3px solid transparent',
                          fontWeight: project.isMaintenance ? 600 : undefined,
                        }}>
                          <td style={{ ...TD, textAlign: 'left', padding: '5px 10px', whiteSpace: 'nowrap' }}>
                            {project.parentProjectId != null && <span style={{ color: 'var(--ew-n-400)', marginRight: 4 }}>↳</span>}
                            {/* ⚠️ A maintenance SUB-PROJECT is not linked, and shows
                                "sub" rather than a job number for the same reason:
                                it is schedule-only, has no job number, and its
                                Overview is not somewhere anybody is meant to go.
                                Linking it would offer a door into a room that
                                isn't furnished. */}
                            {project.parentProjectId != null ? (
                              <span style={{ fontSize: 11, color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums' }}>sub</span>
                            ) : (
                              <Link
                                to={`/projects/${project.projectId}`}
                                className="mp-row-link"
                                style={{ fontSize: 11, color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums' }}
                                title={`Open ${project.jobNumber} — ${project.title}`}
                              >
                                {project.jobNumber}
                              </Link>
                            )}
                          </td>
                          <td style={{ ...TD, textAlign: 'left', padding: '5px 10px', paddingLeft: project.parentProjectId != null ? 26 : 10, maxWidth: canEdit ? 280 : 170, overflow: canEdit ? 'visible' : 'hidden', textOverflow: canEdit ? 'clip' : 'ellipsis', whiteSpace: canEdit ? 'normal' : 'nowrap', position: 'relative' }}>
                            {project.parentProjectId != null ? (
                              <span style={{ fontWeight: 500, fontSize: 12, fontStyle: 'italic' }}>{project.title}</span>
                            ) : (
                              <Link
                                to={`/projects/${project.projectId}`}
                                className="mp-row-link"
                                style={{ fontWeight: 500, fontSize: 12 }}
                                title={`Open ${project.jobNumber} — ${project.title}`}
                              >
                                {project.title}
                              </Link>
                            )}
                            {pct != null && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 8, verticalAlign: 'middle' }} title="Blended completion — same labor-hour weighting as the project Overview">
                                <span style={{ display: 'inline-block', width: 54, height: 6, borderRadius: 'var(--ew-r-pill)', background: 'var(--ew-n-100)', overflow: 'hidden' }}>
                                  <span style={{ display: 'block', width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: 'var(--ew-green)' }} />
                                </span>
                                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ew-green-700)', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                              </span>
                            )}
                            {project.isMaintenance && (
                              <span style={MAINTENANCE_BADGE} title="Maintenance project — can host schedule-only sub-projects.">Maintenance</span>
                            )}
                            {project.isLoanedSpToGc && (
                              <span style={LOAN_BADGE} title="Self-perform labor loaned to this GC project.">
                                SP Loan{project.loanFromDivision ? ` ← ${project.loanFromDivision.label}` : ''}
                              </span>
                            )}
                            {mismatch && (
                              <span style={START_MISMATCH_BADGE}
                                title={`${startMismatchSummary(mismatch, fmtDate)}\n\nThe project's start date is the PM's to change on the Schedule & Dates card; the crew days are the Scheduler's. One of the two needs moving.`}>
                                ⚠ Start ≠ Crew
                              </span>
                            )}
                            {project.isSystac && <span style={SYSTAC_BADGE}>SYSTAC</span>}
                            {project.isUnmatchedImport && !project.isMaintenance && isImportStubJobNumber(project.jobNumber) && (
                              <span style={UNMATCHED_BADGE} title="Created by Excel import without a matching job # — fix on the Projects tab.">
                                ⚠ Needs Job#
                              </span>
                            )}
                            {/* On a pursuit the row already shows crew, so the
                                plain wording read as a contradiction — "needs
                                scheduling" above a run of solid numbers. Since
                                #425 those numbers look exactly like booked crew,
                                so the badge is what says which kind they are. */}
                            {section.isNeedsScheduling && (
                              <span
                                style={NEEDS_SCHEDULING_BADGE}
                                /* ⚠️ "in these weeks", not "yet". This rule reads the
                                   WINDOWED entries, so a job can sit here while carrying
                                   crew booked months out — and the Start ≠ Crew badge
                                   beside it then names that very date. The old wording
                                   said flatly that no crew was booked, which the badge
                                   next to it contradicted (#385). When both apply, this
                                   says where the crew actually is. */
                                title={bidCrewShown
                                  ? 'The crew shown on this row is anticipated from the bid, not booked. It counts toward the totals so you can see the impact if the job is won — but nothing is scheduled yet.'
                                  : mismatch
                                    ? `This project starts in the visible window with no crew booked in these weeks — its first booked day is ${fmtDate(mismatch.firstCrewDate)}.`
                                    : 'This project starts in the visible window but has no crew booked in these weeks.'}
                              >
                                {bidCrewShown ? 'Bid crew — not booked' : 'Needs scheduling'}
                              </span>
                            )}
                            {/* Every row write-action lives behind Edit Schedule. This used to
                                gate on `canEdit` alone, so the row-admin cluster (remove crew,
                                ＋ Sub, tags, delete sub) sat on the read-only board where an
                                editor could fire a destructive action without ever entering
                                edit mode. Read view is now genuinely read-only. */}
                            {canEdit && editMode && (
                              <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                {/* Shift only on rows that carry their own crew — not a
                                    header-only maintenance parent. */}
                                {!project.isMaintenance && rowEditable(project) && (<>
                                  <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setShiftState({ projectId: project.projectId, offsetDays: shiftState?.projectId === project.projectId ? shiftState.offsetDays : 7 })}>⇄ Shift</button>
                                  {shiftState?.projectId === project.projectId && (<>
                                    <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setShiftState((s) => s ? { ...s, offsetDays: s.offsetDays - 1 } : s)}>-</button>
                                    <span style={{ fontSize: 10, fontWeight: 700 }}>{shiftState.offsetDays}d</span>
                                    <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setShiftState((s) => s ? { ...s, offsetDays: s.offsetDays + 1 } : s)}>+</button>
                                    <button className="btn btn-primary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => void shiftProjectRow(project)}>Apply</button>
                                  </>)}
                                </>)}

                                {/* Row admin (clear crew / sub-projects / tags) hits
                                    full-editor routes — hidden for fab-scoped editors. */}
                                {!fabEditOnly && (project.parentProjectId != null ? (
                                  /* Sub-project: delete it outright (with confirm). */
                                  deleteSubConfirm === project.projectId ? (<>
                                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ew-danger)', whiteSpace: 'nowrap' }}>Delete sub-project?</span>
                                    <button className="btn btn-danger" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => deleteSubProjectMutation.mutate(project.projectId, { onSuccess: () => setDeleteSubConfirm(null) })}>Yes</button>
                                    <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setDeleteSubConfirm(null)}>No</button>
                                  </>) : (
                                    <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px', color: 'var(--ew-danger)' }} title="Delete this sub-project and its schedule entries" onClick={() => setDeleteSubConfirm(project.projectId)}>🗑 Delete sub</button>
                                  )
                                ) : (<>
                                  {/* Real project: clear future crew, tag menu, and (if maintenance) add a sub.
                                      A maintenance parent carries no crew of its own, so it skips the clear-crew action. */}
                                  {!project.isMaintenance && (
                                    removeConfirm === project.projectId ? (<>
                                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ew-danger)', whiteSpace: 'nowrap' }}>Clear crew from today on?</span>
                                      <button className="btn btn-danger" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => void clearProjectFuture(project)}>Yes</button>
                                      <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setRemoveConfirm(null)}>No</button>
                                    </>) : (
                                      <button
                                        className="btn btn-secondary"
                                        style={{ fontSize: 10, padding: '2px 6px', color: 'var(--ew-danger)' }}
                                        title="Remove from Manpower — clears this project's crew from today forward (past weeks kept)"
                                        aria-label={`Remove ${project.title} from the manpower schedule`}
                                        onClick={() => setRemoveConfirm(project.projectId)}
                                      >
                                        ✕
                                      </button>
                                    )
                                  )}
                                  {project.isMaintenance && (
                                    <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} title="Add a schedule-only sub-project under this maintenance project" onClick={() => { setAddingSubTo(project.projectId); setSubName(''); }}>＋ Sub</button>
                                  )}
                                  {!project.isMaintenance && (
                                    <button
                                      className="btn btn-secondary"
                                      style={{ fontSize: 10, padding: '2px 6px' }}
                                      title="Set up crew for the whole job at once — stages a draft you can then fine-tune cell by cell"
                                      aria-label={`Set up crew for ${project.title}`}
                                      onClick={() => setSetupTarget(project)}
                                    >
                                      📅 Set up
                                    </button>
                                  )}
                                  <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} title="Maintenance / loaned-labor tags" onClick={() => setRowMenu(rowMenu === project.projectId ? null : project.projectId)}>🏷</button>
                                </>))}
                              </span>
                            )}

                            {/* Edit-mode stepper for single-crew rows (fab
                                jobs carry theirs on the phase lines below). */}
                            {!fabSplit && crewStepper(crewRows[0])}

                            {/* Inline add-sub-project input */}
                            {canEdit && addingSubTo === project.projectId && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }} onClick={(e) => e.stopPropagation()}>
                                <input
                                  autoFocus value={subName} onChange={(e) => setSubName(e.target.value)}
                                  placeholder="Sub-project name"
                                  onKeyDown={(e) => { if (e.key === 'Enter' && subName.trim()) createSubProjectMutation.mutate({ parentId: project.projectId, name: subName.trim() }); if (e.key === 'Escape') setAddingSubTo(null); }}
                                  style={{ fontSize: 11, padding: '2px 6px', border: '1px solid var(--ew-n-300)', borderRadius: 4, minWidth: 160 }}
                                />
                                <button className="btn btn-primary" style={{ fontSize: 10, padding: '2px 6px' }} disabled={!subName.trim() || createSubProjectMutation.isPending} onClick={() => createSubProjectMutation.mutate({ parentId: project.projectId, name: subName.trim() })}>Add</button>
                                <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setAddingSubTo(null)}>Cancel</button>
                              </span>
                            )}

                            {/* Tag menu: maintenance + GC-loan toggles */}
                            {canEdit && rowMenu === project.projectId && (
                              <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 10, marginTop: 4, background: 'var(--ew-white)', border: '1px solid var(--ew-n-200)', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.15)', padding: 10, minWidth: 240, whiteSpace: 'normal' }} onClick={(e) => e.stopPropagation()}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={!!project.isMaintenance} onChange={(e) => setMaintenanceMutation.mutate({ projectId: project.projectId, isMaintenance: e.target.checked })} />
                                  Maintenance project (can host sub-projects)
                                </label>
                                <div style={{ borderTop: '1px solid var(--ew-n-100)', margin: '8px 0' }} />
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={!!project.isLoanedSpToGc} onChange={(e) => setGcLoanMutation.mutate({ projectId: project.projectId, isLoaned: e.target.checked, loanFromDivisionId: project.loanFromDivision?.id ?? null })} />
                                  Loaned self-perform labor → GC
                                </label>
                                {project.isLoanedSpToGc && (
                                  <div style={{ marginTop: 6, paddingLeft: 22, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                    <span style={{ color: 'var(--ew-n-500)' }}>From:</span>
                                    <select
                                      value={project.loanFromDivision?.id ?? ''}
                                      onChange={(e) => setGcLoanMutation.mutate({ projectId: project.projectId, isLoaned: true, loanFromDivisionId: e.target.value ? Number(e.target.value) : null })}
                                      style={{ fontSize: 12, padding: '2px 6px' }}
                                    >
                                      <option value="">— Select division —</option>
                                      {divisionOptions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                                    </select>
                                  </div>
                                )}
                                <div style={{ textAlign: 'right', marginTop: 8 }}>
                                  <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setRowMenu(null)}>Close</button>
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Primary PM. Sub-projects inherit their parent's
                              job in practice, so the column is left blank on
                              them rather than repeating the parent's PM. */}
                          <td style={{ ...TD, textAlign: 'left', padding: '5px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110, fontSize: 11, color: project.pm ? 'var(--ew-n-700)' : 'var(--ew-n-300)' }} title={project.pm ?? undefined}>
                            {project.parentProjectId != null ? '' : (project.pm ?? '—')}
                          </td>

                          {/* Travel / Local. */}
                          <td style={{ ...TD, textAlign: 'left', padding: '4px 8px', whiteSpace: 'nowrap' }}>
                            <TypeToggle
                              project={project}
                              isTravel={travelJobOf(project)}
                              canEdit={canEdit && editMode && !fabEditOnly}
                              pending={setTravelJobMutation.isPending && setTravelJobMutation.variables?.projectId === project.projectId}
                              onToggle={(travelJob) => setTravelJobMutation.mutate({ projectId: project.projectId, travelJob })}
                            />
                          </td>

                          {fabSplit ? (
                            /* Fab job header row carries no cells — crew lives
                               on the two phase lines below. */
                            <td colSpan={columns.length} style={{ ...TD, background: 'var(--ew-n-50)' }} />
                          ) : (
                            renderCrewCells(crewRows[0])
                          )}
                        </tr>

                        {/* Fabrication / Installation crew lines */}
                        {fabSplit && crewRows.map((crewRow) => {
                          const meta = PHASE_META[crewRow.phase as 'fabrication' | 'installation'];
                          return (
                            <tr key={crewRow.key} style={{ background: 'var(--ew-white)', borderLeft: '3px solid transparent' }}>
                              <td style={{ ...TD }} />
                              <td style={{ ...TD, textAlign: 'left', padding: '4px 10px 4px 26px', whiteSpace: 'nowrap' }}>
                                <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: meta.tick, marginRight: 6, verticalAlign: 'middle' }} />
                                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ew-n-500)' }}>{meta.label}</span>
                                {crewStepper(crewRow)}
                              </td>
                              <td style={{ ...TD }} />
                              {/* Type — carried by the job's header row above,
                                  not repeated on its two phase lines. */}
                              <td style={{ ...TD }} />
                              {renderCrewCells(crewRow)}
                            </tr>
                          );
                        })}
                        </Fragment>
                        );
                      })}

                      {/* Section subtotal — skipped for "Needs Scheduling" since
                          its rows have no crew by definition (an all-zero
                          subtotal would just be visual noise). */}
                      {!section.isNeedsScheduling && (
                        <tr style={{ background: headerBg, borderTop: '1px dashed var(--ew-n-200)' }}>
                          <td colSpan={LABEL_COLS} style={{
                            padding: '5px 12px', fontSize: 10, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: headerFg, whiteSpace: 'nowrap', textAlign: 'right',
                          }}>
                            {section.label} Subtotal
                          </td>
                          {columns.map((col, i) => {
                            const total = colValue(subtotal, col);
                            return (
                              <td key={i} style={{
                                padding: '5px 3px', textAlign: 'center', fontSize: 11, fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                borderLeft: col.isWeekStart && i > 0 ? '2px solid var(--ew-n-200)' : undefined,
                                color: total === 0 ? 'var(--ew-n-300)' : headerFg,
                              }}>
                                {total > 0 ? total : ''}
                              </td>
                            );
                          })}
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>

            {/* Footer — Headroom pinned to the bottom. The Grand Total moved
                to the sticky header tier (PM request: overall number at the
                top); duplicating it here too would just be noise. */}
            <tfoot>
              {/* Fab lens: factual shop-vs-field crew totals. (Capacity
                  composition — borrowed/temps — will be TRACKED data, not
                  derived tiers; see FabPhaseTotals.tsx for why.) */}
              {scope === 'fab' && (
                <FabPhaseTotalsRows
                  columns={columns}
                  colValue={colValue}
                  fabTotals={fabTotals}
                  installTotals={installTotals}
                />
              )}
              <tr style={{ background: '#1e1e1e', color: 'var(--ew-white)' }}>
                <td colSpan={LABEL_COLS} style={{ padding: '7px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ew-n-400)', whiteSpace: 'nowrap', position: 'sticky', bottom: 0, background: '#1e1e1e', zIndex: 4 }}>
                  Headroom
                </td>
                {columns.map((col, i) => {
                  const total = colValue(colTotals, col);
                  const delta = availableMp - total;
                  const isNeg = delta < 0;
                  return (
                    <td key={i} style={{
                      padding: '7px 3px', textAlign: 'center', fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      position: 'sticky', bottom: 0, background: '#1e1e1e', zIndex: 3,
                      borderLeft: col.isWeekStart && i > 0 ? '2px solid rgba(255,255,255,0.1)' : undefined,
                      color: total === 0 ? 'var(--ew-n-500)' : isNeg ? '#fca5a5' : '#86efac',
                    }}>
                      {total === 0 ? '—' : (isNeg ? '' : '+') + delta}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Legend */}
      <div style={{ marginTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11, color: 'var(--ew-n-400)', alignItems: 'center' }}>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderLeft: '3px solid var(--ew-performance)', marginRight: 4, verticalAlign: 'middle' }} />
          SYSTAC project
        </span>
        {scope === 'fab' && (
          <>
            <span>
              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: 'var(--ew-dark)', marginRight: 4, verticalAlign: 'middle' }} />
              Fabrication (shop)
            </span>
            <span>
              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: 'var(--ew-quality)', marginRight: 4, verticalAlign: 'middle' }} />
              Installation (field)
            </span>
          </>
        )}
        {/* Green scale swatches */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {[1, 3, 6, 10, 16, 22].map((n) => {
            const fill = crewFillStyle(n);
            return <span key={n} title={`~${n}`} style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 'var(--ew-r-sm)', background: fill.background as string, border: '1px solid rgba(0,0,0,0.08)' }} />;
          })}
          <span style={{ marginLeft: 4 }}>crew density</span>
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 'var(--ew-r-sm)', background: 'var(--ew-n-100)', border: '1px solid var(--ew-n-200)', marginRight: 4, verticalAlign: 'middle' }} />
          Weekend / non-work
        </span>
        {editMode && (
          <>
            <span title="Color = kind of change (orange pin, blue plain edit); dashed = staged, solid = live">
              <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 'var(--ew-r-sm)', outline: '2px dashed var(--ew-quality)', outlineOffset: -2, marginRight: 4, verticalAlign: 'middle' }} />
              Unpublished change
            </span>
            <span>
              <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 'var(--ew-r-sm)', outline: '2px dashed var(--ew-performance)', outlineOffset: -2, marginRight: 4, verticalAlign: 'middle' }} />
              Pinned day · staged
            </span>
            <span>
              <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 'var(--ew-r-sm)', outline: '2px solid var(--ew-performance)', outlineOffset: -2, marginRight: 4, verticalAlign: 'middle' }} />
              Pinned day · live (steppers skip it · retype the base rate to un-pin)
            </span>
          </>
        )}
        <span style={{ color: 'var(--ew-safety)' }}>⚠ Yellow totals = over capacity</span>
        <span style={{ color: '#86efac' }}>+ Green headroom = remaining</span>
        {/* Instructions for using the screen — every branch of this tells the
            reader which control to reach for, so none of it survives onto
            paper. The colour keys above it do, because they explain the grid
            itself. */}
        <span className="mp-noprint">
          {editMode
            ? 'Click cell to edit · Enter/Tab saves · Esc cancels · changes stage until Publish'
            : daily
              ? (canEdit ? 'Read-only — use ✎ Edit Schedule to make changes' : 'Read-only view')
              : 'Weekly peak crew/day · switch to 2 or 4 wk to edit days'}
        </span>
      </div>
      </>
      )}

      {/* Import modal */}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            queryClient.invalidateQueries({ queryKey: ['manpower-schedule', fromParam, toParam] });
            // The import writes PUBLISHED entries, so it can move a job's first
            // booked crew day -- what the start-mismatch flag reads (#385).
            void invalidateStartAlignmentQueries(queryClient);
          }}
        />
      )}

      {/* Sync modal */}
      {showSync && <SyncModal onClose={() => setShowSync(false)} />}

      {/* Bulk crew setup — stages the whole job as a draft in one call, then
          the scheduler adjusts individual cells as normal. */}
      {setupTarget && (
        <CrewSetupModal
          project={setupTarget}
          windowStart={dateStrs[0]}
          todayYmd={localYmd(today)}
          busy={drafts.bulk.isPending}
          onClose={() => setSetupTarget(null)}
          onApply={(res: CrewSetupResult) => {
            const label = `${setupTarget.jobNumber} · ${setupTarget.title}`;
            drafts.bulk.mutate(res.entries, {
              onSuccess: () => {
                setSetupTarget(null);
                setNotifications((prev) => [
                  `Staged crew for ${label} across ${res.dayCount} day${res.dayCount === 1 ? '' : 's'}`
                    + `${res.replacedCount > 0 ? ` (replaced ${res.replacedCount} existing day cell${res.replacedCount === 1 ? '' : 's'})` : ''}`
                    + `${res.clearedCount > 0 ? ` (cleared ${res.clearedCount} upcoming day cell${res.clearedCount === 1 ? '' : 's'} the new pattern doesn’t cover)` : ''}`
                    + ' — publish to make it live.',
                  ...prev,
                ].slice(0, 3));
              },
            });
          }}
        />
      )}

      {/* Help modal */}
      {showHelp && <ScheduleHelpModal canEdit={canEdit} onClose={() => setShowHelp(false)} />}
    </div>
  );
}

/**
 * Travel / Local on a crew-schedule row (#396).
 *
 * ⚠️ THIS WRITES `projects.travel_job`, the company's one classification — the
 * same field the project Overview's "Travel job" checkbox edits. That is what
 * The manpower lead asked for: one place to say what a job is, and every screen agreeing.
 * The Gap Analysis tab splits every chart, every gap and both job tables on
 * it, so a change here moves that tab immediately (the mutation invalidates
 * it).
 *
 * ⚠️ A SUB-PROJECT SHOWS ITS PARENT'S ANSWER AND CANNOT BE SET. Every read
 * resolves a child's travel status through `COALESCE(pp.travel_job,
 * p.travel_job)`, so a value written on a child is one nothing reads — the API
 * refuses it for that reason, and offering a control the server will reject is
 * how somebody spends ten minutes wondering why the board will not move.
 *
 * `isTravel` is therefore RESOLVED BY THE CALLER (travelJobOf), never read off
 * `project.travelJob` here. A child's own bit is always 0 and showing it put a
 * "Local" badge under a Travel maintenance parent whose crew this very app was
 * counting as travel.
 *
 * Two buttons rather than a checkbox or a dropdown: the choice is binary and
 * both options should be readable without opening anything, because the whole
 * column exists to be scanned.
 */
function TypeToggle({ project, isTravel, canEdit, pending, onToggle }: {
  project: ProjectRow;
  /** Effective classification — the parent's on a sub-project. */
  isTravel: boolean;
  canEdit: boolean;
  pending: boolean;
  onToggle: (travelJob: boolean) => void;
}) {
  const isSub = project.parentProjectId != null;

  const base: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, padding: '2px 7px', lineHeight: 1.5,
    border: '1px solid var(--ew-n-200)', cursor: 'pointer', fontFamily: 'var(--ew-font)',
    background: 'var(--ew-white)', color: 'var(--ew-n-400)',
  };
  const on: React.CSSProperties = {
    background: 'var(--ew-quality)', borderColor: 'var(--ew-quality)', color: 'var(--ew-white)',
  };

  if (!canEdit || isSub) {
    return (
      <span
        style={{
          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--ew-r-pill)',
          background: isTravel ? 'var(--ew-quality)' : 'var(--ew-n-100)',
          color: isTravel ? 'var(--ew-white)' : 'var(--ew-n-500)',
          opacity: isSub ? 0.6 : 1,
        }}
        title={isSub
          ? `Inherited from the parent job. A sub-project has no classification of its own — set it on the parent row.`
          : 'Travel or Local. Editable by the scheduler in Edit Schedule.'}
      >
        {isTravel ? 'Travel' : 'Local'}
      </span>
    );
  }

  return (
    <span
      role="group"
      aria-label={`Job type for ${project.title}`}
      style={{ display: 'inline-flex', borderRadius: 'var(--ew-r-pill)', overflow: 'hidden', opacity: pending ? 0.5 : 1 }}
    >
      {([false, true] as const).map((travel) => {
        const active = isTravel === travel;
        return (
          <button
            key={String(travel)}
            type="button"
            // ⚠️ The print stylesheet hides every button on this page — a
            // control is an invitation paper cannot take. That would have left
            // the Type column BLANK on a printout taken in edit mode, while
            // the read-only view (a span) printed it fine: the same sheet
            // saying two different things depending on a mode the reader
            // cannot see. Keeping the ACTIVE half prints exactly one word, the
            // classification, which is what the read-only pill prints too.
            className={active ? 'mp-print-keep' : undefined}
            disabled={pending}
            aria-pressed={active}
            title={travel
              ? 'Travel job — counts toward the traveling crew and traveling leader figures on Gap Analysis.'
              : 'Local job — counts toward the local crew and local leader figures on Gap Analysis.'}
            // Already this value: don't fire a no-op PATCH. The endpoint is
            // idempotent and logs nothing when the flag has not moved, but a
            // round trip that repaints the grid for nothing still looks broken.
            onClick={() => { if (!active) onToggle(travel); }}
            style={{ ...base, ...(active ? on : null) }}
          >
            {travel ? 'Travel' : 'Local'}
          </button>
        );
      })}
    </span>
  );
}
